import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Box,
  Grid,
  Button,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ThemeProvider,
  DialogContentText,
} from "@mui/material";
import {
  PhotoCamera as PhotoIcon,
  Fingerprint as FingerprintIcon,
  Description as BondIcon,
  Diamond as DiamondIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { useBranchCreateGuard } from "../BranchCreateGuard";
import { PawnModeContext } from "../../Context/PawnModeContext";
import CustomerLookupDialog from "./CustomerLookupDialog";
import NewCustomerDialog from "./NewCustomerDialog";
import { DocumentViewerDialog } from "../TravelERP/DocumentViewerDialog";
import { useNavigate, useLocation } from "react-router-dom";
import { normalizeImageFile } from "../../utils/imageCapture";
import {
  schemeBaseRateForMode,
  rateForLoanAmount,
} from "../../utils/pawnInterestCalculator";

import {
  API_BASE_URL,
  BLUE,
  BLUE_DARK,
  BLUE_MED,
  INK,
  INK_SOFT,
  INK_MUTED,
  SURFACE,
  BG,
  validateMobile,
  validateAadhar,
  validatePAN,
  OFFICIAL_RATE_CAP,
  realDocFile,
  appTheme,
} from "./paymentScreen/shared";
import { fetchItemTypes } from "./itemTypes";

import PageHeader from "./paymentScreen/PageHeader";
import { useCustomerTxnLookup } from "./customerTxnLookup";
import CustomerSection from "./paymentScreen/CustomerSection";
import OrnamentsSection from "./paymentScreen/OrnamentsSection";
import LoanPaymentSection from "./paymentScreen/LoanPaymentSection";
import DocumentsSection from "./paymentScreen/DocumentsSection";
import SummaryPanel from "./paymentScreen/SummaryPanel";
import BluetoothPrinterDialog from "./paymentScreen/BluetoothPrinterDialog";
import { parseLabel } from "./barcodeMapper/prnParse";
import { usePawnEditAccess, VIEW_ONLY_MESSAGE } from "./pawnEditAccess";
import {
  labelToSVG,
  builtInStickerSVG,
  labelPrintHTML,
  templateFitsMedia,
  mediaOptionsFor,
  mediaFromKey,
  mediaKey,
  readLabelPref,
  writeLabelPref,
  DEFAULT_LABEL_MM,
  LABEL_MEDIA_KEY,
  LABEL_DESIGN_KEY,
} from "./barcodeMapper/labelSvg";
import { buildLabelBytes } from "./barcodeMapper/labelRaster";
import {
  btPrint,
  btRadioAvailable,
  btSupport,
  getBtState,
  loadBtSettings,
  reconnectBtPrinter,
  subscribeBtPrinter,
} from "./paymentScreen/btPrinter";

// The Purity % an ornament row opens on for a metal: the metal's own Default
// Purity from the Metal Type master (91.6 on the seeded GOLD). Since purities
// stopped being records of their own this is the only default there is —
// a metal without one simply starts blank and the teller types the figure.
const defaultPurityOf = (metal) => {
  const pct = parseFloat(metal?.def_purity);
  return Number.isFinite(pct) && pct > 0 ? pct : "";
};

// A loan date fit to key a rate against. A native date input reports every
// half-finished year as it is typed ("0002-08-21" on the way to 2026), and
// stamping a rate onto the year 2 would put it in front of every real loan
// forever — so the rate lookup and the rate write both wait for a real date.
const isUsableLoanDate = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value || "") &&
  Number(value.slice(0, 4)) >= 1900 &&
  dayjs(value).isValid();

// ─── MAIN PAYMENT SCREEN ─────────────────────────────────────────────────────
const PaymentScreen = () => {
  const { currentStoreInfo, userInfo, defaultFY, defaultFYId, branchScope } =
    useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;
  const userId = userInfo?.id;

  // Raising a loan needs one branch to record it against — see handleSave.
  const {
    blocked: branchBlocked,
    openBranchPicker,
    guardDialog: branchGuardDialog,
  } = useBranchCreateGuard({
    reason:
      "You are viewing every branch. Pick the branch this loan is being " +
      "raised at before saving it.",
  });
  const navigate = useNavigate();
  const location = useLocation();
  // Guards the one-shot auto-load of a ticket passed in via navigation state
  // (e.g. clicking a payment on the dashboard) so it fires only once per entry.
  const autoLoadedVoucherRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // ── States (identical to original) ──────────────────────────────────────────
  // Mode is now owned globally by PawnModeContext (switched only via the hidden
  // F12 gesture). This screen reads the mode but never sets it: loading a ticket
  // of the other series is rejected as "not found" rather than flipping modes.
  const { mode: pawnMode, modeLoaded } = useContext(PawnModeContext);
  const [currentFormattedVoucherNo, setCurrentFormattedVoucherNo] =
    useState(null);
  const [loading, setLoading] = useState(false);
  const [openSections, setOpenSections] = useState({
    1: true,
    2: false,
    3: false,
    4: false,
  });
  const [customer, setCustomer] = useState(null);
  const [nomineesList, setNomineesList] = useState([]);
  const [selectedNominee, setSelectedNominee] = useState(null);
  // When set, the combined customer/nominee dialog opens straight on the
  // Nominee tab to add a nominee for this already-selected customer.
  const [dialogExistingCustomerId, setDialogExistingCustomerId] =
    useState(null);
  const [dialogExistingCustomerName, setDialogExistingCustomerName] =
    useState("");
  const [schemes, setSchemes] = useState([]);
  const [floorsList, setFloorsList] = useState([]);
  const [ornaments, setOrnaments] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  // True once the user types a value manually for the current ornament, which
  // suppresses auto-calculation so their entered value is preserved.
  const [valueEdited, setValueEdited] = useState(false);
  const [cashAccountId, setCashAccountId] = useState(null);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  // Bluetooth label printer paired with *this* device (phone/tablet). Only the
  // live connection state is held in React — the connection itself, and the
  // settings, belong to the btPrinter module so they survive this screen.
  const [btPrinterOpen, setBtPrinterOpen] = useState(false);
  const [btState, setBtState] = useState(getBtState);
  const [suggestions, setSuggestions] = useState({
    name: [],
    mobile: [],
    guardian: [],
    aadhar: [],
    pan: [],
  });
  const [suggestionLoading, setSuggestionLoading] = useState({
    name: false,
    mobile: false,
    guardian: false,
    aadhar: false,
    pan: false,
  });
  const [activeField, setActiveField] = useState(null);
  // Suggestion-dropdown anchors live in CustomerSection (tracked with ref
  // callbacks) — the section unmounts when its accordion collapses, so a node
  // captured here would go stale and mis-position the dropdown.
  const [formData, setFormData] = useState({
    voucherNo: "",
    date: dayjs().format("YYYY-MM-DD"),
    paymentTime: dayjs().format("HH:mm:ss"),
    customerId: "",
    customerName: "",
    mobile: "",
    phone: "",
    address: "",
    aadhar: "",
    pan: "",
    district: "",
    ledgerNo: "",
    sinNo: "",
    loanAmount: "",
    itemValue: "",
    tareWeight: "",
    scheme: "",
    interestRate: "",
    insuranceRate: "",
    interestAmount: "",
    otherCharges: "",
    paymentAmount: "",
    payMode: "CASH",
    chequeNo: "",
    nominee: "",
    remarks: "",
    sonOf: "",
    collectByDate: dayjs().add(1, "year").format("YYYY-MM-DD"),
    floorId: "",
  });
  const [currentOrnament, setCurrentOrnament] = useState({
    si: 0,
    metal: "",
    metal_type_id: "",
    stamp: false,
    itemName: "",
    pieces: 1,
    grossWeight: "",
    netWeight: "",
    purity: "",
    metal_type_rate: "",
    value: "",
  });
  const [errors, setErrors] = useState({});
  const [metalTypes, setMetalTypes] = useState([]);
  // Item Type Master rows for the whole store — deleted ones too, so typing a
  // deleted name offers to restore it. The picker narrows to the metal.
  const [itemTypes, setItemTypes] = useState([]);
  const [itemTypeError, setItemTypeError] = useState(false);
  const [selectedMetalId, setSelectedMetalId] = useState("");
  const [ratePerGram, setRatePerGram] = useState("");
  // Where the rate on screen came from: the date it was keyed against and
  // whether that is the loan date itself or an earlier one it carried forward
  // from. Only used to caption the Rate field, so a teller entering an old loan
  // can see the figure belongs to another day before pledging against it.
  const [rateSource, setRateSource] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [pawnSettings, setPawnSettings] = useState(null);
  const [deductOneMonth, setDeductOneMonth] = useState(false);
  const [currentVoucherNo, setCurrentVoucherNo] = useState(null);
  // ── Manual voucher number (create-only, gated by the store setting) ──────────
  // When manual_voucher_no is on in Pawn Settings, the voucher number on this
  // screen becomes editable while creating a NEW payment. Each keystroke is
  // checked against existing loans; a clash flags the field and blocks
  // Save/Print. `checking` is true while a check is in flight, `taken` is true
  // when the entered number already belongs to another loan.
  const [voucherCheck, setVoucherCheck] = useState({
    checking: false,
    taken: false,
  });
  // Monotonic id so a slow duplicate-check response can't overwrite a newer one.
  const voucherCheckSeqRef = useRef(0);
  // The auto-sequence next voucher number, held apart from what the header
  // shows. In manual mode the header field stays EMPTY until the teller types a
  // number — pre-filling it meant a save with nothing typed silently used the
  // auto number, which is exactly what the setting exists to prevent. Ticket
  // navigation still needs a number to step back from, so we keep it here.
  const [autoVoucherNo, setAutoVoucherNo] = useState(null);
  // True once the teller has typed in the manual voucher field, so a late
  // settings/voucher fetch can never wipe what they entered.
  const voucherTouchedRef = useRef(false);
  // Red-outlines the manual voucher field after a Save was attempted with it
  // empty (house rule: block the save, warn, and mark the offending field).
  const [voucherWarn, setVoucherWarn] = useState(false);
  // Latest settings in a ref: fetchNextVoucher can resolve before the settings
  // fetch does, and it must know then whether manual mode is on.
  const pawnSettingsRef = useRef(null);
  // ── Edit-existing-payment state ──────────────────────────────────────────────
  // When editMode is true the screen is editing an existing pawn payment
  // (loaded via the ticket-number box in the header) instead of creating a new
  // one. editingPawnId is the pawn_broking row id being edited.
  const [editMode, setEditMode] = useState(false);
  const [editingPawnId, setEditingPawnId] = useState(null);
  // True when the currently-loaded payment has one or more receipts raised
  // against it. Such a payment is view-only: editing it would alter the
  // principal the receipt chain depends on. The ticket still loads normally so
  // the teller can view it and keep navigating; the form is just read-only.
  const [paymentLocked, setPaymentLocked] = useState(false);
  // Whether the loaded loan is fully settled (is_settled = "Y"). Distinguishes
  // the read-only reason: a settled loan shows "Already settled loan" while a
  // merely receipt-bearing (unsettled) loan shows the generic receipt message.
  const [loanSettled, setLoanSettled] = useState(false);
  // Store Staff may raise a payment but not change one already on file: a
  // loaded payment is view-only for them, exactly like a receipted one, with
  // the Save/Update control removed rather than greyed. See pawnEditAccess.js.
  const canEditRecords = usePawnEditAccess();
  const roleLocked = editMode && !canEditRecords;
  const formLocked = paymentLocked || roleLocked;
  // Secret reveal of the true interest rate for above-cap loaded vouchers
  // (F2+O gesture — same as the receipt screen). Handhelds have no function
  // keys, so a double tap on the Interest group header does the same thing.
  const [revealInterest, setRevealInterest] = useState(false);
  const toggleRevealInterest = useCallback(
    () => setRevealInterest((v) => !v),
    [],
  );
  const [ticketSearch, setTicketSearch] = useState("");
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [splitPayment, setSplitPayment] = useState(false);
  const [secondAccountId, setSecondAccountId] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [bankAmount, setBankAmount] = useState("");
  // ── Second bank/account-transfer leg (revealed via "Add Another") ────────────
  // When split payment is on the customer can be paid via cash + up to two
  // bank/account transfers. The second leg is optional.
  const [secondBankEnabled, setSecondBankEnabled] = useState(false);
  const [thirdAccountId, setThirdAccountId] = useState("");
  const [bankAmount2, setBankAmount2] = useState("");
  // Reference numbers for the split legs: transfer 1 (leg 2) →
  // transaction_ref_no_1, and transfer 2 (leg 3) → transaction_ref_no_2.
  const [utrNumber, setUtrNumber] = useState("");
  const [txnRefNumber, setTxnRefNumber] = useState("");
  const [images, setImages] = useState({
    cust_img: null,
    article_img_1: null,
    article_img_2: null,
    cust_ID_img: null,
  });
  const [imageFileNames, setImageFileNames] = useState({
    cust_img: "",
    article_img_1: "",
    article_img_2: "",
    cust_ID_img: "",
  });
  const [imageSourceDialog, setImageSourceDialog] = useState({
    open: false,
    index: null,
  });
  // Scanned fingerprints for this ticket, one entry per finger. Each value is
  // either null ("not scanned" — the column stays NULL) or
  // { data: <base64>, source, quality, format }, where `data` is what the
  // backend decodes into pawn_broking.fingerprint_1 / fingerprint_2.
  const [fingerprints, setFingerprints] = useState({
    fingerprint_1: null,
    fingerprint_2: null,
  });
  const [fingerprintDialog, setFingerprintDialog] = useState({
    open: false,
    field: null,
  });
  // Image field key currently capturing from the live camera.
  const [cameraField, setCameraField] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ── Reusable confirmation dialog (replaces window.confirm) ───────────────────
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    confirmColor: "primary",
    onConfirm: null,
  });
  const requestConfirm = (opts) =>
    setConfirmState({
      open: true,
      title: opts.title || "Are you sure?",
      message: opts.message || "",
      confirmLabel: opts.confirmLabel || "Confirm",
      confirmColor: opts.confirmColor || "primary",
      onConfirm: opts.onConfirm || null,
    });
  const closeConfirm = () => setConfirmState((p) => ({ ...p, open: false }));
  const handleConfirmAccept = () => {
    const fn = confirmState.onConfirm;
    closeConfirm();
    if (typeof fn === "function") fn();
  };

  const nomineeOptions = formData.customerId
    ? [...nomineesList, { nominee_id: "add_nominee", name: "Add Nominee" }]
    : [];

  const imageFields = [
    { key: "cust_img", label: "Customer Photo", icon: <PhotoIcon /> },
    { key: "cust_ID_img", label: "ID Proof", icon: <BondIcon /> },
    { key: "article_img_1", label: "Article Photo 1", icon: <PhotoIcon /> },
    { key: "article_img_2", label: "Article Photo 2", icon: <PhotoIcon /> },
  ];

  // Two fingers are recorded alongside the photos. Both are optional.
  const fingerprintFields = [
    { key: "fingerprint_1", label: "Finger Print 1" },
    { key: "fingerprint_2", label: "Finger Print 2" },
  ];

  // ── Refs ─────────────────────────────────────────────────────────────────────
  // Nominee loaded with a pawn ticket, stashed for the nominee-list fetch that
  // the customer-id change triggers right after. Without this the async fetch
  // lands AFTER the ticket load and overwrites the ticket's nominee with the
  // customer's primary nominee (or null) — which is why the nominee only
  // appeared on the SECOND load of the same voucher. Shape: {custId, nominee}.
  const pendingNomineeRef = useRef(null);
  // Keys currently held + whether F2+O fired during this F2 hold (see the
  // F1/F2 keyboard effect).
  const pressedKeysRef = useRef(new Set());
  const f2ComboFiredRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const nameInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const rateUpdateTimeoutRef = useRef(null);
  const isFetchingRateRef = useRef(false);
  const formRef = useRef(null);
  const addOrnamentBtnRef = useRef(null);
  // The ornament row's item picker (typedText / settleTyped), and whether an
  // Add is waiting on it to create or pick the item type first.
  const itemTypePickerRef = useRef(null);
  const addAfterItemTypeRef = useRef(false);
  const saveButtonRef = useRef(null);
  const loanAmountRef = useRef(null);
  // Gross value that last auto-filled the net weight (or the loaded gross when
  // editing an ornament). Enter on an unchanged gross must not clobber a
  // hand-edited net; null means "no sync yet" so the first Enter always fills.
  const grossAtNetSyncRef = useRef(null);

  // ── Snackbar ─────────────────────────────────────────────────────────────────
  const showSnackbar = (message, severity = "success") => {
    setSnackbar((prev) => {
      if (prev.open) return { ...prev, open: false };
      return prev;
    });
    setTimeout(() => setSnackbar({ open: true, message, severity }), 100);
  };
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const getFilteredAccounts = useCallback(() => {
    if (!pawnSettings || !bankAccounts.length) return bankAccounts;
    const excludeIds = [
      pawnSettings.pledge_acc,
      pawnSettings.interest_acc,
      pawnSettings.insurance_acc,
      pawnSettings.other_charges_acc,
    ].filter(Boolean);
    return bankAccounts.filter((acc) => !excludeIds.includes(acc.account_id));
  }, [bankAccounts, pawnSettings]);

  const getCashAccountId = useCallback(
    () => pawnSettings?.cash_acc || cashAccountId || null,
    [pawnSettings, cashAccountId],
  );

  // The UPI-transfer account from Pawn Settings is only the DEFAULT for the
  // first account-transfer leg (leg 2) — the user can pick any other bank.
  const getUpiAccountId = useCallback(
    () => pawnSettings?.UPI_acc || null,
    [pawnSettings],
  );

  const urlToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  // ── Fetchers (identical logic) ────────────────────────────────────────────────
  const fetchSchemes = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/schemes/${storeId}`,
      );
      if (res.data.success) setSchemes(res.data.data);
    } catch {
      showSnackbar("Failed to load schemes", "error");
    }
  }, [storeId]);

  const fetchFloors = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/floors/store/${storeId}`,
      );
      if (res.data.success) setFloorsList(res.data.data || []);
    } catch {
      // Non-blocking — floor selection is optional
    }
  }, [storeId]);

  const fetchMetalTypes = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/metal-type-master/store/${storeId}`,
      );
      if (res.data.success) {
        const metals = res.data.data.filter(
          (m) => m.is_active && !m.is_deleted,
        );
        setMetalTypes(metals);
        if (metals.length > 0) {
          setSelectedMetalId(metals[0].id);
          setCurrentOrnament((prev) => ({
            ...prev,
            metal: metals[0].name,
            metal_type_id: metals[0].id,
            purity: defaultPurityOf(metals[0]),
          }));
        }
      }
    } catch {
      showSnackbar("Failed to load metal types", "error");
    }
  }, [storeId]);

  const loadItemTypes = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    try {
      const { rows } = await fetchItemTypes(storeId, { status: "all" });
      setItemTypes(rows);
    } catch {
      showSnackbar("Failed to load item types", "error");
    }
  }, [storeId]);

  // A type created from the ornament row joins the list at once (replacing
  // the row when it was a restore of a deleted one).
  const handleItemTypeCreated = (row) =>
    setItemTypes((prev) => [...prev.filter((t) => t.id !== row.id), row]);

  // The rate in force for the selected metal ON THE LOAN DATE.
  //
  // Rates belong to a metal and a date, never to a purity: the figure stored is
  // the 100 %-pure rate and each ornament applies its own Purity % on top of
  // it. The date is the loan date rather than today, which is what lets a shop
  // key in its old books — a loan dated 28.12.2012 saved at 7000 makes 7000 the
  // rate every later loan opens with until a newer date is keyed. The backend
  // answers with the newest rate on or before the date asked for, so the 29th,
  // 30th and 31st all still price at the 28th's figure.
  const fetchRateForSelection = useCallback(async () => {
    if (storeId === undefined || storeId === null || !selectedMetalId) {
      setRatePerGram("");
      setRateSource(null);
      return;
    }
    // Mid-typing dates are not worth a round trip — the finished date fires
    // this again a keystroke later. Leave whatever is on screen alone.
    if (!isUsableLoanDate(formData.date)) return;
    isFetchingRateRef.current = true;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/jewel-master/rates/${storeId}/metal/${selectedMetalId}`,
        { params: { on: formData.date } },
      );
      const found = response.data?.success ? response.data.data : null;
      if (found && found.rate != null) {
        const fetchedRate = found.rate.toString();
        setRatePerGram(fetchedRate);
        setRateSource({
          date: found.record_date,
          exact: !!response.data.is_exact,
        });
        setCurrentOrnament((prev) => ({
          ...prev,
          metal_type_rate: parseFloat(fetchedRate),
        }));
      } else {
        // This metal has never been priced on or before the loan date. Leave
        // the field empty rather than showing a later rate — whatever the
        // teller types becomes the rate FROM this date onwards.
        setRatePerGram("");
        setRateSource(null);
      }
    } catch (error) {
      showSnackbar("Failed to fetch rate", "error");
      setRatePerGram("");
      setRateSource(null);
    } finally {
      isFetchingRateRef.current = false;
    }
  }, [storeId, selectedMetalId, formData.date]);

  const fetchNextVoucher = async () => {
    if (storeId === undefined || storeId === null) {
      return;
    }

    try {
      const params = { store_id: storeId, mode: pawnMode };

      if (pawnMode === 2) {
        if (!defaultFYId) {
          console.warn("No default financial year ID for unofficial mode");
          showSnackbar(
            "Financial year not set. Please select a financial year in Pawn Settings.",
            "error",
          );
          return;
        }
        params.fy_id = defaultFYId;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/pawn/settings/next-voucher-no`,
        { params },
      );

      if (response.data && response.data.success) {
        const rawNumber = response.data.voucherNo;
        const formattedNumber = response.data.displayVoucherNo;
        // Always remembered — ticket navigation steps back from it even when
        // the header field itself is left blank for the teller to fill in.
        setAutoVoucherNo(formattedNumber);
        // Manual-voucher mode: the number is the teller's to type, so the field
        // starts empty and Save stays barred until they do.
        if (pawnSettingsRef.current?.manual_voucher_no) {
          voucherTouchedRef.current = false;
          setVoucherWarn(false);
          setCurrentVoucherNo(null);
          setCurrentFormattedVoucherNo("");
          setFormData((prev) => ({ ...prev, voucherNo: "" }));
          return;
        }
        setCurrentVoucherNo(rawNumber);
        setCurrentFormattedVoucherNo(formattedNumber);
        setFormData((prev) => ({
          ...prev,
          voucherNo: rawNumber, // keep raw for internal use
        }));
      } else {
        console.error("Invalid response format:", response.data);
        showSnackbar("Failed to get voucher number", "error");
      }
    } catch (error) {
      console.error("Failed to fetch voucher number:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      showSnackbar(
        error.response?.data?.message || "Failed to get voucher number",
        "error",
      );
    }
  };

  // Stores the rate the teller typed against the metal AND the loan date, so it
  // becomes the rate every later loan opens with. Re-saving the same date
  // overwrites: retyping a figure is a correction, not a second rate.
  const saveRateForMetal = useCallback(
    async (newRate, recordDate) => {
      if (storeId === undefined || storeId === null || !selectedMetalId) {
        return null;
      }
      const parsed = parseFloat(newRate);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      const response = await axios.put(
        `${API_BASE_URL}/api/jewel-master/rates/${storeId}/metal/${selectedMetalId}`,
        {
          rate: parsed,
          rate_without_gst: null,
          record_date: recordDate,
        },
      );
      return response.data;
    },
    [storeId, selectedMetalId],
  );

  const fetchBankAccounts = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    setLoadingAccounts(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/accounts/accounts/${storeId}/accounts-only`,
      );
      if (response.data.success && response.data.data) {
        const allAccounts = response.data.data;
        setBankAccounts(allAccounts);
        const cashAccId = pawnSettings?.cash_acc;
        if (cashAccId) {
          const cashAccountExists = allAccounts.some(
            (acc) => acc.account_id === cashAccId,
          );
          if (cashAccountExists) {
            setCashAccountId(cashAccId);
            setFormData((prev) => ({ ...prev, payMode: cashAccId }));
          } else if (allAccounts.length > 0)
            setFormData((prev) => ({
              ...prev,
              payMode: allAccounts[0].account_id,
            }));
        } else if (allAccounts.length > 0)
          setFormData((prev) => ({
            ...prev,
            payMode: allAccounts[0].account_id,
          }));
      } else setBankAccounts([]);
    } catch {
      showSnackbar("Failed to load accounts", "error");
      setBankAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, [storeId, pawnSettings?.cash_acc]);

  const fetchSuggestions = async (field, value) => {
    if (!value || value.trim().length < 2) {
      setSuggestions((prev) => ({ ...prev, [field]: [] }));
      setActiveField(null);
      return;
    }
    setSuggestionLoading((prev) => ({ ...prev, [field]: true }));
    const params = {
      search_mode: "wild",
      sort: "ASC",
      limit: 10,
      search: "",
      aadhar: "",
      pan: "",
      relative_name: "",
    };
    if (field === "name" || field === "mobile") params.search = value.trim();
    else if (field === "guardian") params.relative_name = value.trim();
    else if (field === "aadhar") params.aadhar = value.trim();
    else if (field === "pan") params.pan = value.trim();
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}`,
        { params },
      );
      if (res.data.success && res.data.data) {
        setSuggestions((prev) => ({ ...prev, [field]: res.data.data }));
        setActiveField(res.data.data.length > 0 ? field : null);
      } else {
        setSuggestions((prev) => ({ ...prev, [field]: [] }));
        setActiveField(null);
      }
    } catch {
      setSuggestions((prev) => ({ ...prev, [field]: [] }));
      setActiveField(null);
    } finally {
      setSuggestionLoading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSearchableFieldChange = (field, value, formKey) => {
    let processedValue = value;
    let isValid = true;

    // Apply validations based on field type
    if (formKey === "mobile") {
      const validation = validateMobile(value);
      isValid = validation.isValid;
      processedValue = validation.value;
      if (validation.error) {
        showSnackbar(validation.error, "warning");
      }
    } else if (formKey === "aadhar") {
      const validation = validateAadhar(value);
      isValid = validation.isValid;
      processedValue = validation.value;
      if (validation.error) {
        showSnackbar(validation.error, "warning");
      }
    } else if (formKey === "pan") {
      const validation = validatePAN(value);
      isValid = validation.isValid;
      processedValue = validation.value;
      if (validation.error) {
        showSnackbar(validation.error, "warning");
      }
    }

    setFormData((prev) => ({ ...prev, [formKey]: processedValue }));
    if (formData.customerId) {
      setFormData((prev) => ({ ...prev, customerId: "" }));
      setCustomer(null);
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(
      () => fetchSuggestions(field, processedValue),
      300,
    );
  };

  const handleFieldFocus = (field) => {
    const valueMap = {
      name: formData.customerName,
      mobile: formData.mobile,
      guardian: formData.sonOf,
      aadhar: formData.aadhar,
      pan: formData.pan,
    };
    const val = valueMap[field] || "";
    if (val.trim().length >= 2 && suggestions[field].length > 0)
      setActiveField(field);
  };

  const closeSuggestions = () => setActiveField(null);

  const selectCustomer = async (selectedCustomer) => {
    const customerId = selectedCustomer.customer_id;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}/id/${customerId}`,
      );
      if (res.data.success) {
        const fullCustomer = res.data.data;
        const aadharDoc = fullCustomer.customer_documents?.find(
          (doc) => doc.name === "Aadhar",
        );
        const panDoc = fullCustomer.customer_documents?.find(
          (doc) => doc.name === "PAN",
        );

        // Apply validation to aadhar and pan when loading
        let aadharValue = aadharDoc?.number || "";
        let panValue = panDoc?.number || "";

        const aadharValidation = validateAadhar(aadharValue);
        if (aadharValidation.error) {
          showSnackbar(`Aadhar: ${aadharValidation.error}`, "warning");
          aadharValue = aadharValidation.value;
        }

        const panValidation = validatePAN(panValue);
        if (panValidation.error) {
          showSnackbar(`PAN: ${panValidation.error}`, "warning");
          panValue = panValidation.value;
        }

        setFormData((prev) => ({
          ...prev,
          customerId: fullCustomer.customer_id,
          customerName: fullCustomer.name,
          mobile: fullCustomer.mobile?.toString() || "",
          phone: fullCustomer.phone || "",
          address: fullCustomer.address || fullCustomer.pickupAddress || "",
          aadhar: aadharValue,
          pan: panValue,
          district: fullCustomer.district || "",
          ledgerNo: fullCustomer.ledgerNo || "",
          sonOf:
            fullCustomer.relative_relation && fullCustomer.relative_name
              ? `${fullCustomer.relative_relation} ${fullCustomer.relative_name}`
              : fullCustomer.relative_name || fullCustomer.sonOf || "",
        }));
        setImages({
          cust_img: null,
          article_img_1: null,
          article_img_2: null,
          cust_ID_img: null,
        });
        setImageFileNames({
          cust_img: "",
          article_img_1: "",
          article_img_2: "",
          cust_ID_img: "",
        });
        // A different customer means any scan taken so far belongs to someone
        // else — drop both fingers.
        setFingerprints({ fingerprint_1: null, fingerprint_2: null });
        if (fullCustomer.customer_documents?.length) {
          let photoUrl = null,
            aadharUrl = null,
            panUrl = null;
          for (const doc of fullCustomer.customer_documents) {
            if (doc.name === "Photo") photoUrl = realDocFile(doc.file);
            if (doc.name === "Aadhar") aadharUrl = realDocFile(doc.file);
            if (doc.name === "PAN") panUrl = realDocFile(doc.file);
          }
          if (photoUrl) {
            const b64 = await urlToBase64(photoUrl);
            if (b64) {
              setImages((p) => ({ ...p, cust_img: b64 }));
              setImageFileNames((p) => ({
                ...p,
                cust_img: "existing_photo.jpg",
              }));
            }
          }
          const idUrl = aadharUrl || panUrl;
          if (idUrl) {
            const b64 = await urlToBase64(idUrl);
            if (b64) {
              setImages((p) => ({ ...p, cust_ID_img: b64 }));
              setImageFileNames((p) => ({
                ...p,
                cust_ID_img: "existing_id_proof.jpg",
              }));
            }
          }
        }
        setCustomer(fullCustomer);
        setSuggestions({
          name: [],
          mobile: [],
          guardian: [],
          aadhar: [],
          pan: [],
        });
        setActiveField(null);
        showSnackbar(`Customer selected: ${fullCustomer.name}`, "success");
        if (fullCustomer.ledgerNo) fetchExistingPawn(fullCustomer.ledgerNo);
      } else showSnackbar("Failed to load full customer details", "error");
    } catch {
      showSnackbar("Could not load customer documents", "error");
    }
  };

  const fetchExistingPawn = async (ledgerNo) => {
    try {
      const res = await axios.get(`/api/pawn/active`, { params: { ledgerNo } });
      if (res.data.pawn) {
        const pawn = res.data.pawn;
        setFormData((prev) => ({
          ...prev,
          sinNo: pawn.sinNo,
          loanAmount: pawn.loanAmount,
        }));
        if (pawn.ornaments) setOrnaments(pawn.ornaments);
        showSnackbar("Existing pawn record found", "info");
      }
    } catch {}
  };

  const fetchPawnSettings = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pawn/settings/${storeId}`,
      );
      if (response.data.success) {
        setPawnSettings(response.data.data);
        pawnSettingsRef.current = response.data.data;
        // set master_int, master_ins into form
        //
        // Numbers, not the DECIMAL(10,3) strings the API sends ("1.000"). React
        // compares a number input's incoming value LOOSELY against the DOM, so
        // "1.000" != 1 is false and a later write of the number 1 — from the
        // amount-wise slab ladder, say — leaves the box still reading "1.000"
        // even though state holds 1. Seeding a number keeps the field and the
        // state showing the same thing.
        const masterInt = response.data.data.master_int;
        if (masterInt != null)
          setFormData((prev) => ({ ...prev, interestRate: Number(masterInt) }));
        const masterIns = response.data.data.master_ins;
        if (masterIns != null)
          setFormData((prev) => ({
            ...prev,
            insuranceRate: Number(masterIns),
          }));
      } else {
        setPawnSettings(null);
        pawnSettingsRef.current = null;
        showSnackbar(
          response.data.statusMessage || "Failed to load settings",
          "error",
        );
      }
    } catch (error) {
      setPawnSettings(null);
      pawnSettingsRef.current = null;
      showSnackbar("Failed to load pawn settings", "error");
    }
  }, [storeId]);

  const fetchNominees = useCallback(
    async (customerId) => {
      if (!customerId || storeId === undefined || storeId === null) {
        setNomineesList([]);
        setSelectedNominee(null);
        return;
      }
      // Consume the nominee stashed by a ticket load (see pendingNomineeRef).
      // The ref is cleared on every call — it is only meaningful for the fetch
      // that immediately follows the ticket load.
      const takePendingTicketNominee = () => {
        const pending = pendingNomineeRef.current;
        pendingNomineeRef.current = null;
        if (pending && String(pending.custId) === String(customerId))
          return pending.nominee;
        return null;
      };
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/customers/store/${storeId}/id/${customerId}`,
        );
        if (res.data.success && res.data.data.nominees) {
          const nominees = res.data.data.nominees;
          setNomineesList(nominees);
          const ticketNominee = takePendingTicketNominee();
          if (ticketNominee) {
            // Prefer the list's own entry for the same person so the
            // Autocomplete selection matches an option exactly.
            const match = nominees.find(
              (n) => String(n.nominee_id) === String(ticketNominee.nominee_id),
            );
            setSelectedNominee(match || ticketNominee);
          } else {
            const primary = nominees.find((n) => n.is_primary);
            setSelectedNominee(primary || null);
          }
        } else {
          setNomineesList([]);
          setSelectedNominee(takePendingTicketNominee());
        }
      } catch {
        setNomineesList([]);
        setSelectedNominee(takePendingTicketNominee());
      }
    },
    [storeId],
  );

  const calculateOrnamentValue = useCallback(() => {
    // Respect a manually-entered value — don't auto-overwrite it.
    if (valueEdited) return;
    const rate = parseFloat(ratePerGram);
    if (isNaN(rate) || rate <= 0) {
      setCurrentOrnament((prev) => ({ ...prev, value: "" }));
      return;
    }
    const purityPercent = parseFloat(currentOrnament.purity) || 0;
    const netWeight = parseFloat(currentOrnament.netWeight) || 0;
    const calculatedValue = netWeight * (purityPercent / 100) * rate;
    setCurrentOrnament((prev) => ({
      ...prev,
      value: Math.round(calculatedValue),
    }));
  }, [
    ratePerGram,
    currentOrnament.purity,
    currentOrnament.netWeight,
    valueEdited,
  ]);

  // ── Effects ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchMetalTypes();
    loadItemTypes();
  }, [storeId]);

  // Add effect to reset loan/payment fields when ornaments change
  useEffect(() => {
    const totalValue = ornaments.reduce(
      (sum, item) => sum + (parseFloat(item.value) || 0),
      0,
    );
    setFormData((prev) => ({ ...prev, itemValue: totalValue || "" }));

    // Reset loan and payment fields if no ornaments
    if (ornaments.length === 0) {
      setFormData((prev) => ({
        ...prev,
        loanAmount: "",
        paymentAmount: "",
        interestAmount: "",
        insuranceAmount: "",
      }));
      setCashAmount("");
      setBankAmount("");
      setBankAmount2("");
      setSplitPayment(false);
      setSecondAccountId("");
      setSecondBankEnabled(false);
      setThirdAccountId("");
      setUtrNumber("");
      setTxnRefNumber("");
      setDeductOneMonth(false);
    } else {
      // If ornaments exist but total value changed, adjust loan amount
      const currentLoan = parseFloat(formData.loanAmount) || 0;
      if (currentLoan > totalValue && totalValue > 0) {
        const newLoan = Math.min(currentLoan, totalValue);
        const rate = parseFloat(formData.interestRate) || 0;
        const insRate = parseFloat(formData.insuranceRate) || 0;
        setFormData((prev) => ({
          ...prev,
          loanAmount: newLoan,
          interestAmount:
            rate > 0 ? Math.round((newLoan * rate) / 100) : prev.interestAmount,
          insuranceAmount:
            insRate > 0
              ? Math.round((newLoan * insRate) / 100)
              : prev.insuranceAmount,
        }));
      }
    }
  }, [ornaments]);

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key !== "Enter") return;

      const tag = e.target.tagName;

      if (tag === "TEXTAREA" || tag === "BUTTON") return;

      // Skip fields that have their own Enter logic
      if (e.target.dataset.skipEnterTab === "true") {
        return;
      }

      const focusable = Array.from(
        formRef.current?.querySelectorAll(
          "input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled])",
        ) || [],
      ).filter((el) => el.offsetParent !== null);

      const idx = focusable.indexOf(e.target);

      if (idx !== -1 && idx < focusable.length - 1) {
        e.preventDefault();
        focusable[idx + 1].focus();
      }
    };

    const node = formRef.current;

    node?.addEventListener("keydown", handleEnterKey);

    return () => node?.removeEventListener("keydown", handleEnterKey);
  }, []);

  useEffect(() => {
    if (splitPayment) {
      const loanAmt = parseFloat(formData.loanAmount) || 0;
      const intAmt = parseFloat(formData.interestAmount) || 0;
      const insAmt = parseFloat(formData.insuranceAmount) || 0;
      const otherAmt = parseFloat(formData.otherCharges) || 0;
      let totalPayment = loanAmt;
      // Insurance is only deducted (and ledger-posted) together with the
      // one-month interest deduction.
      if (deductOneMonth) totalPayment -= intAmt + insAmt;
      totalPayment -= otherAmt;
      totalPayment = Math.max(0, totalPayment);
      const cAmt = parseFloat(cashAmount) || 0;
      const bAmt = parseFloat(bankAmount) || 0;
      const b2Amt = parseFloat(bankAmount2) || 0;
      // When the underlying total changes, reset the split allocation back to
      // all-cash (the user can re-split). Covers the optional second bank leg.
      if (cAmt + bAmt + b2Amt !== totalPayment && totalPayment > 0) {
        setCashAmount(totalPayment);
        setBankAmount(0);
        setBankAmount2(0);
        setFormData((prev) => ({ ...prev, paymentAmount: totalPayment }));
      }
    }
  }, [
    deductOneMonth,
    formData.loanAmount,
    formData.interestAmount,
    formData.insuranceAmount,
    formData.otherCharges,
    splitPayment,
    pawnSettings,
  ]);

  useEffect(() => {
    if (formData.customerId) fetchNominees(formData.customerId);
    else {
      pendingNomineeRef.current = null;
      setNomineesList([]);
      setSelectedNominee(null);
    }
  }, [formData.customerId, fetchNominees]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // F1 opens the customer lookup. F2 opens the new-customer dialog — but only
  // on RELEASE, because holding F2 and pressing O is the secret gesture that
  // toggles the true interest-rate reveal for above-cap vouchers (the same
  // gesture as the receipt screen). When the combo fires during the hold, the
  // new-customer dialog is suppressed for that press.
  useEffect(() => {
    const norm = (e) =>
      e.key && e.key.length === 1 ? e.key.toLowerCase() : e.key;
    const onKeyDown = (e) => {
      const k = norm(e);
      if (e.key === "F1") {
        e.preventDefault();
        // Locked (receipted) payments are view-only — don't open the customer
        // lookup, which would let the customer be changed.
        if (!formLocked) setLookupOpen(true);
        return;
      }
      if (e.key === "F2") e.preventDefault();
      pressedKeysRef.current.add(k);
      if (pressedKeysRef.current.has("F2") && pressedKeysRef.current.has("o")) {
        e.preventDefault();
        setRevealInterest((v) => !v);
        f2ComboFiredRef.current = true;
        // Consume the O so key-repeat doesn't rapidly re-toggle.
        pressedKeysRef.current.delete("o");
      }
    };
    const onKeyUp = (e) => {
      pressedKeysRef.current.delete(norm(e));
      if (e.key === "F2") {
        // The F2+O reveal gesture stays live (view-only), but a locked
        // payment must not open the new-customer dialog.
        if (!f2ComboFiredRef.current && !formLocked) {
          setDialogExistingCustomerId(null);
          setDialogExistingCustomerName("");
          setNewCustomerOpen(true);
        }
        f2ComboFiredRef.current = false;
      }
    };
    const clear = () => {
      pressedKeysRef.current.clear();
      f2ComboFiredRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", clear);
    };
  }, [formLocked]);

  // Re-mask whenever the operator switches global pawn mode.
  useEffect(() => {
    setRevealInterest(false);
  }, [pawnMode]);

  // ── Abrupt mode switch with a ticket already open ──────────────────────────
  // Official and unofficial are two separate registers, but status 3
  // (official-clean: rate <= 1.16%, no one-month deduct) is deliberately SHARED
  // by both books — so an official ticket can legitimately be sitting on this
  // screen when the operator flips the mode with the hidden F12 gesture.
  //
  // Leaving it there is a trap. The screen is then editing a record of the
  // OTHER book, and because the backend re-derives an edit's status from the
  // saved rate (never from the app mode), the very same Update silently moves
  // the record between the books: save 1.1% and it stays status 3 (visible in
  // both), save 1.5% and it becomes status 1 (official only) and vanishes from
  // the unofficial dashboard the operator is standing in. Nothing is corrupted
  // — the record never leaves the official series — but it is impossible to
  // reason about from the counter.
  //
  // So a mode switch wipes the sheet back to a fresh payment, exactly as if New
  // had been pressed: a loaded ticket is only ever editable from the mode it
  // was loaded in. Mirrored on the Receipt/Settlement screens.
  const loadedInModeRef = useRef(null);
  useEffect(() => {
    const prevMode = loadedInModeRef.current;
    // Track the live mode first — the persisted mode settles asynchronously, so
    // the ref must follow it even on the runs we take no action on.
    if (modeLoaded) loadedInModeRef.current = pawnMode;
    if (!modeLoaded) return;
    // First settle of the persisted mode, or no real switch.
    if (prevMode === null || prevMode === pawnMode) return;
    // Nothing loaded: a half-typed NEW payment is still valid under the new
    // mode — it just re-prices (effect below) and picks up the new series'
    // voucher number, so there is nothing to protect.
    if (!editMode) return;
    // A pending confirm ("Discard changes?") targets the ticket that is going
    // away; drop it rather than let it fire against a fresh form.
    closeConfirm();
    clearAllFormData({ modeSwitched: true });
    // Deliberately keyed on the mode alone: editMode/clearAllFormData are read
    // from the render that follows the switch, and the ref guard is what makes
    // this fire exactly once per switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pawnMode, modeLoaded]);

  // A scheme carries one rate per mode, so switching mode mid-entry must re-price
  // the selected scheme (official ⇒ Interest, unofficial ⇒ Interest 2). Only for
  // a NEW payment — a loaded ticket keeps the rate it was booked at.
  useEffect(() => {
    if (editMode || !formData.scheme || !schemes.length) return;
    const scheme = schemes.find((s) => s.id === formData.scheme);
    if (!scheme) return;
    const rate = schemeBaseRateForMode(scheme, Number(pawnMode) === 2);
    if (rate === (parseFloat(formData.interestRate) || 0)) return;
    setFormData((prev) => ({ ...prev, interestRate: rate }));
    calculateInterest(rate);
    // Re-price only on a mode switch / scheme list refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pawnMode, schemes, editMode]);

  useEffect(() => {
    // While editing an existing payment we keep that ticket's voucher number —
    // don't replace it with the next-new-voucher number.
    if (editMode) return;
    if ((storeId !== undefined || storeId !== null) && pawnMode && modeLoaded) {
      if (pawnMode === 2 && !defaultFYId) return;
      fetchNextVoucher();
    }
  }, [storeId, pawnMode, modeLoaded, defaultFYId, editMode]);

  useEffect(() => {
    fetchPawnSettings();
  }, [fetchPawnSettings]);

  // Live duplicate-check for a manually-typed voucher number. Runs only when
  // manual-voucher mode is active (setting on + creating, not editing). Debounced
  // so we fire one request after the teller pauses, and guarded with a sequence
  // id so a stale response can't clobber a newer keystroke's result.
  useEffect(() => {
    const manualMode = !!pawnSettings?.manual_voucher_no && !editMode;
    if (!manualMode || storeId == null) {
      setVoucherCheck({ checking: false, taken: false });
      return;
    }
    const val = String(currentFormattedVoucherNo ?? "").trim();
    if (!val) {
      setVoucherCheck({ checking: false, taken: false });
      return;
    }
    const seq = ++voucherCheckSeqRef.current;
    setVoucherCheck((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/payments/check-voucher/${storeId}`,
          { params: { voucher_no: val, mode: pawnMode } },
        );
        if (seq !== voucherCheckSeqRef.current) return; // superseded
        setVoucherCheck({
          checking: false,
          taken: !!(res.data && res.data.success && res.data.exists),
        });
      } catch (e) {
        if (seq !== voucherCheckSeqRef.current) return;
        // On a network error don't hard-block the teller — treat as not taken.
        setVoucherCheck({ checking: false, taken: false });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [pawnSettings, editMode, storeId, pawnMode, currentFormattedVoucherNo]);

  // The settings fetch and the next-voucher fetch race each other on mount. If
  // the voucher number landed first it is sitting in the header; clear it once
  // the settings say manual mode is on. Only ever clears the auto-fetched
  // number itself — a loaded ticket's voucher or a teller-typed one is left
  // alone (the touched ref covers typing the auto number by hand).
  useEffect(() => {
    if (!pawnSettings?.manual_voucher_no || editMode) return;
    if (voucherTouchedRef.current || !autoVoucherNo) return;
    if (currentFormattedVoucherNo !== autoVoucherNo) return;
    setCurrentVoucherNo(null);
    setCurrentFormattedVoucherNo("");
    setFormData((prev) => ({ ...prev, voucherNo: "" }));
  }, [pawnSettings, editMode, autoVoucherNo, currentFormattedVoucherNo]);

  useEffect(() => {
    if (!pawnSettings) return;
    const oneMonthDeductEnabled = !!pawnSettings.one_month_deduct;
    const loanAmt = parseFloat(formData.loanAmount) || 0;
    const intAmt = parseFloat(formData.interestAmount) || 0;
    const insAmt = parseFloat(formData.insuranceAmount) || 0;
    const otherAmt = parseFloat(formData.otherCharges) || 0;
    let newPaymentAmount = loanAmt;
    // Insurance is only deducted (and ledger-posted) together with the
    // one-month interest deduction; other charges always reduce the payout.
    if (oneMonthDeductEnabled && deductOneMonth)
      newPaymentAmount = Math.max(0, loanAmt - intAmt - insAmt);
    newPaymentAmount = Math.max(0, newPaymentAmount - otherAmt);
    if (newPaymentAmount !== (parseFloat(formData.paymentAmount) || 0))
      setFormData((prev) => ({
        ...prev,
        paymentAmount: newPaymentAmount || "",
      }));
  }, [
    pawnMode,
    pawnSettings,
    deductOneMonth,
    formData.loanAmount,
    formData.interestAmount,
    formData.insuranceAmount,
    formData.otherCharges,
  ]);

  useEffect(() => {
    if (storeId !== undefined && storeId !== null && pawnSettings)
      fetchBankAccounts();
  }, [storeId, pawnSettings, fetchBankAccounts]);

  // Auto-load a pawn ticket passed via navigation state (e.g. clicking a
  // payment on the dashboard). We wait until the prerequisites the manual
  // ticket search also relies on are ready — store, pawn mode, settings and
  // bank accounts (payment-split reconstruction needs the resolved cash
  // account) — then replay loadPawnForEdit exactly once per navigation.
  useEffect(() => {
    const voucher = location.state?.voucher_no;
    if (!voucher) return;
    if (autoLoadedVoucherRef.current === voucher) return;
    if (
      storeId == null ||
      !modeLoaded ||
      !pawnSettings ||
      cashAccountId == null
    )
      return;
    autoLoadedVoucherRef.current = voucher;
    setTicketSearch(voucher);
    loadPawnForEdit(voucher);
    // loadPawnForEdit / setTicketSearch are stable enough for this one-shot;
    // re-running only when the prerequisites resolve is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, storeId, modeLoaded, pawnSettings, cashAccountId]);

  useEffect(() => {
    calculateOrnamentValue();
  }, [
    ratePerGram,
    currentOrnament.purity,
    currentOrnament.netWeight,
    calculateOrnamentValue,
  ]);

  useEffect(() => {
    const loanAmt = parseFloat(formData.loanAmount) || 0;
    const itemVal = parseFloat(formData.itemValue) || 0;
    if (loanAmt > itemVal)
      setErrors((prev) => ({
        ...prev,
        loanAmount: "Loan amount exceeds total ornament value",
      }));
    else setErrors((prev) => ({ ...prev, loanAmount: undefined }));
  }, [formData.loanAmount, formData.itemValue]);

  useEffect(() => {
    if (modeLoaded && (storeId !== undefined || storeId !== null))
      fetchSchemes();
  }, [modeLoaded, storeId, pawnMode]);
  useEffect(() => {
    fetchFloors();
  }, [fetchFloors]);
  // Re-price whenever the metal or the LOAN DATE changes — moving the date back
  // to 2012 must bring 2012's rate with it, not leave today's on screen.
  useEffect(() => {
    fetchRateForSelection();
  }, [fetchRateForSelection, selectedMetalId, formData.date]);

  // Mouse-wheel over a focused number input must scroll, not change the value
  // (the spinner arrows are hidden via CSS; this kills the wheel increment).
  useEffect(() => {
    const stopNumberWheel = (e) => {
      const t = e.target;
      if (
        t instanceof HTMLInputElement &&
        t.type === "number" &&
        document.activeElement === t
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener("wheel", stopNumberWheel, { passive: false });
    return () => document.removeEventListener("wheel", stopNumberWheel);
  }, []);

  // Ctrl+Enter / Cmd+Enter triggers Save from anywhere on the form
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        saveButtonRef.current?.click();
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // ── Handlers (identical logic) ────────────────────────────────────────────────

  const handleCashAmountChange = (value) => {
    let safeValue = Math.max(0, parseFloat(value) || 0);
    const total = getSplitTotal();
    const cash = Math.min(safeValue, total);
    const bank = total - cash;
    setCashAmount(cash);
    setBankAmount(bank);
    setFormData((prev) => ({ ...prev, paymentAmount: total }));
  };

  const handleBankAmountChange = (value) => {
    const total = getSplitTotal();
    const bank = Math.min(value, total);
    const cash = total - bank;
    setCashAmount(cash);
    setBankAmount(bank);
    setFormData((prev) => ({ ...prev, paymentAmount: total }));
  };

  // ── Split total (loan minus deductions) ────────────────────────────────────
  // Interest AND insurance are deducted only when "Deduct 1 month interest" is
  // on (insurance is never deducted or ledger-posted without it); other
  // charges always reduce the payout.
  const getSplitTotal = () => {
    const loanAmt = parseFloat(formData.loanAmount) || 0;
    const intAmt = parseFloat(formData.interestAmount) || 0;
    const insAmt = parseFloat(formData.insuranceAmount) || 0;
    const otherAmt = parseFloat(formData.otherCharges) || 0;
    let total = loanAmt;
    if (deductOneMonth) total -= intAmt + insAmt;
    total -= otherAmt;
    return Math.max(0, total);
  };

  // When a second bank leg is in play the three amounts (cash + bank-1 + bank-2)
  // stay auto-balanced exactly like the two-field case: cash is the balancing
  // leg, so entering a bank amount reduces cash to keep the legs summing to the
  // total. No single leg may exceed the loan value — if a larger amount is
  // entered it auto-fills to the maximum the balance allows.
  const handleCash3 = (value) => {
    const total = getSplitTotal();
    const b2 = parseFloat(bankAmount2) || 0;
    let cash = Math.min(Math.max(0, parseFloat(value) || 0), total - b2);
    const b1 = Math.max(0, total - cash - b2); // bank-1 absorbs the remainder
    setCashAmount(cash);
    setBankAmount(b1);
    setFormData((prev) => ({ ...prev, paymentAmount: total }));
  };
  const handleBank1_3 = (value) => {
    const total = getSplitTotal();
    const b2 = parseFloat(bankAmount2) || 0;
    let b1 = Math.min(Math.max(0, parseFloat(value) || 0), total - b2);
    const cash = Math.max(0, total - b1 - b2); // cash decreases to balance
    setBankAmount(b1);
    setCashAmount(cash);
    setFormData((prev) => ({ ...prev, paymentAmount: total }));
  };
  const handleBank2_3 = (value) => {
    const total = getSplitTotal();
    const b1 = parseFloat(bankAmount) || 0;
    let b2 = Math.min(Math.max(0, parseFloat(value) || 0), total - b1);
    const cash = Math.max(0, total - b1 - b2); // cash decreases to balance
    setBankAmount2(b2);
    setCashAmount(cash);
    setFormData((prev) => ({ ...prev, paymentAmount: total }));
  };

  // Reveal / hide the optional second bank leg.
  const handleAddSecondBank = () => {
    const cashAccId = getCashAccountId();
    const used = [secondAccountId, cashAccId].filter(Boolean);
    const nextAccount = getFilteredAccounts().find(
      (acc) => acc.account_id !== cashAccId && !used.includes(acc.account_id),
    );
    if (nextAccount) setThirdAccountId(nextAccount.account_id);
    setSecondBankEnabled(true);
  };
  const handleRemoveSecondBank = () => {
    setSecondBankEnabled(false);
    setThirdAccountId("");
    setBankAmount2("");
    setTxnRefNumber("");
  };

  const addOrnament = async () => {
    // Mandatory ornament fields: item name, gross weight, net weight, value.
    // (Rate and purity are optional — the value can be entered directly.)
    const grossW = parseFloat(currentOrnament.grossWeight) || 0;
    const netW = parseFloat(currentOrnament.netWeight) || 0;
    const ornValueNum = parseFloat(currentOrnament.value) || 0;
    const missing = [];
    // The item must be an Item Type Master row. Text typed into the picker but
    // never chosen is NOT "missing" from the teller's point of view — they
    // typed it — so the picker settles it instead of us nagging (see below).
    const hasItemType = Boolean(currentOrnament.item_type_id);
    const typedItem = itemTypePickerRef.current?.typedText() || "";
    if (!hasItemType && !typedItem) missing.push("item type");
    if (!(grossW > 0)) missing.push("gross weight");
    if (!(netW > 0)) missing.push("net weight");
    if (!(ornValueNum > 0)) missing.push("value");
    setItemTypeError(!hasItemType && !typedItem);
    if (missing.length) {
      showSnackbar(`Please fill ${missing.join(", ")}`, "warning");
      return;
    }
    // Everything else is in; only the typed item is unsettled. The picker
    // either picks the exact match or reopens Create for it — and once the
    // item type lands on the ornament, the effect below finishes this Add.
    if (!hasItemType) {
      addAfterItemTypeRef.current = true;
      itemTypePickerRef.current?.settleTyped({ forAdd: true });
      return;
    }

    // Rate is optional now. Prefer the live rate, then the ornament's own rate,
    // otherwise derive it from the entered value when a purity % is available.
    let currentRate = ratePerGram;
    if (
      (!currentRate || currentRate === "") &&
      currentOrnament.metal_type_rate
    ) {
      currentRate = currentOrnament.metal_type_rate.toString();
    }
    let rate = parseFloat(currentRate);
    if (isNaN(rate) || rate <= 0) {
      const purityForRate = parseFloat(currentOrnament.purity) || 0;
      rate =
        netW > 0 && purityForRate > 0
          ? ornValueNum / (netW * (purityForRate / 100))
          : 0;
    }

    // The ornament's own Purity %. There is no purity record behind it — the
    // field is the whole story, and valuation multiplies by it directly.
    const purityPercent = parseFloat(currentOrnament.purity) || 0;

    // Ensure value is calculated
    let ornamentValue = currentOrnament.value;
    if (!ornamentValue || ornamentValue === "") {
      ornamentValue = Math.round(
        (parseFloat(currentOrnament.netWeight) || 0) *
          (purityPercent / 100) *
          rate,
      );
    }

    if (editingIndex !== null) {
      const updated = [...ornaments];
      updated[editingIndex] = {
        ...currentOrnament,
        si: editingIndex + 1,
        purity: purityPercent,
        metal_type_rate: rate,
        value: ornamentValue,
      };
      setOrnaments(updated);
      showSnackbar("Ornament updated", "success");
      setEditingIndex(null);
      setTimeout(() => {
        loanAmountRef.current?.focus();
      }, 100);
    } else {
      setOrnaments([
        ...ornaments,
        {
          ...currentOrnament,
          si: ornaments.length + 1,
          purity: purityPercent,
          metal_type_rate: rate,
          value: ornamentValue,
        },
      ]);
      showSnackbar("Ornament added", "success");
      // Focus Loan Amount
      setTimeout(() => {
        loanAmountRef.current?.focus();
      }, 100);
    }

    // Reset form but keep the metal, purity and rate — the next piece in the
    // same pouch is almost always the same metal at the same purity.
    setCurrentOrnament({
      si: 0,
      metal: currentOrnament.metal,
      metal_type_id: currentOrnament.metal_type_id,
      stamp: false,
      itemName: "",
      pieces: 1,
      grossWeight: "",
      netWeight: "",
      purity: currentOrnament.purity,
      metal_type_rate: rate,
      value: "",
    });
    grossAtNetSyncRef.current = null;
    setValueEdited(false); // resume auto-calc for the next ornament
  };

  const editOrnament = (index) => {
    setCurrentOrnament(ornaments[index]);
    setEditingIndex(index);
    // The loaded gross is the baseline: Enter on it unchanged keeps the
    // stored net; only a genuine gross edit re-syncs net.
    grossAtNetSyncRef.current = parseFloat(ornaments[index].grossWeight) || 0;
    // When a live rate is available, allow auto-recalculation so changing the
    // weight/purity updates the value. Only preserve the stored value when
    // there is no rate to recompute from (a manually-entered value).
    setValueEdited(!(parseFloat(ratePerGram) > 0));
    window.scrollTo({
      top: document.getElementById("ornament-form").offsetTop,
      behavior: "smooth",
    });
  };

  // Finishes an Add that was waiting on the item type (see addOrnament). Runs
  // after the render that put the id on the ornament, so addOrnament here sees
  // the settled ornament.
  useEffect(() => {
    if (addAfterItemTypeRef.current && currentOrnament.item_type_id) {
      addAfterItemTypeRef.current = false;
      addOrnament();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrnament.item_type_id]);

  const deleteOrnament = (index) => {
    requestConfirm({
      title: "Remove ornament",
      message: "Remove this ornament from the list?",
      confirmLabel: "Remove",
      confirmColor: "error",
      onConfirm: () => {
        const updated = ornaments.filter((_, i) => i !== index);
        setOrnaments(updated?.map((item, idx) => ({ ...item, si: idx + 1 })));
        showSnackbar("Ornament removed", "info");
      },
    });
  };

  // ── Amount-wise interest ───────────────────────────────────────────────────
  // A store on the amount basis does not choose a rate — the size of the loan
  // does, from the ladder in Settings → Interest Slab. So every path that
  // changes the loan amount re-asks the ladder.
  //
  // The rate is snapshotted onto the loan (loan_interest) with no scheme_id,
  // which is what makes every later receipt price from that snapshot rather
  // than from a scheme — editing the ladder never re-prices a live loan.
  const amountWiseInterest =
    (pawnSettings?.interest_slab_mode || "month") === "amount";
  const amountSlabs = pawnSettings?.amount_slabs || [];

  /**
   * Fill in the rate the ladder gives for `loanAmount`, and the interest with
   * it. Returns the rate applied, or null when it did not apply — the caller
   * then keeps whatever rate is in the field, so a month-wise store and a store
   * that has not built its ladder yet behave exactly as before.
   *
   * A blank or zero amount is left alone deliberately: clearing the field to
   * retype it should not overwrite a rate the teller typed by hand.
   */
  const applyAmountSlabRate = (loanAmount) => {
    if (!amountWiseInterest) return null;
    const amt = parseFloat(loanAmount);
    if (!Number.isFinite(amt) || amt <= 0) return null;
    const rate = rateForLoanAmount(amt, amountSlabs);
    if (rate === null) return null;
    setFormData((prev) => ({
      ...prev,
      interestRate: rate,
      // No scheme on this basis — and a saved scheme_id would make every later
      // receipt re-price at the SCHEME rate, silently overriding the slab.
      scheme: "",
    }));
    calculateInterest(rate, amt);
    return rate;
  };

  // Which band the current loan amount landed in, for the hint under the
  // Interest % field: a teller who can see "₹10,000 and above" does not have to
  // open Settings to know why the rate is what it is.
  const interestSlabHint = (() => {
    if (!amountWiseInterest || amountSlabs.length === 0) return "";
    const amt = parseFloat(formData.loanAmount);
    if (!Number.isFinite(amt) || amt <= 0) return "Rate follows the loan amount";
    const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
    // Same order and the same inclusive bounds as rateForLoanAmount, so the
    // band named here is always the band whose rate was applied.
    const ladder = amountSlabs
      .slice()
      .sort((a, b) => parseFloat(a.from_amount) - parseFloat(b.from_amount));
    const band = ladder.find((s) => {
      const from = parseFloat(s.from_amount) || 0;
      const noCeiling =
        s.to_amount === null || s.to_amount === undefined || s.to_amount === "";
      return amt >= from && (noCeiling || amt <= parseFloat(s.to_amount));
    });
    // The slab ranges are free-form, so they may leave an amount uncovered. Say
    // so — otherwise the rate silently stays at whatever was in the box and
    // nobody finds out until the interest looks wrong.
    if (!band) return "No slab covers this amount";
    const from = parseFloat(band.from_amount) || 0;
    const noCeiling =
      band.to_amount === null ||
      band.to_amount === undefined ||
      band.to_amount === "";
    return noCeiling
      ? `${money(from)} and above`
      : `${money(from)} – ${money(parseFloat(band.to_amount))}`;
  })();

  const calculateLoanAmount = () => {
    const itemVal = Math.max(0, parseFloat(formData.itemValue) || 0);
    let newLoan = Math.round(itemVal * 0.7);
    if (newLoan > itemVal) newLoan = itemVal;
    if (newLoan < 0) newLoan = 0;
    setFormData((prev) => ({ ...prev, loanAmount: newLoan }));
    const insRate = Math.max(0, parseFloat(formData.insuranceRate) || 0);
    if (applyAmountSlabRate(newLoan) === null) {
      const rate = Math.max(0, parseFloat(formData.interestRate) || 0);
      if (rate > 0) calculateInterest(rate, newLoan);
    }
    if (insRate > 0) calculateInsurance(insRate, newLoan);
  };

  // Quick-fill the loan amount from a chosen LTV % of the ornament value.
  // Frontend convenience only — the saved payload is unchanged (loan_amount as before).
  const applyLtv = (pct) => {
    const itemVal = Math.max(0, parseFloat(formData.itemValue) || 0);
    if (itemVal <= 0) return;
    let newLoan = Math.round((itemVal * pct) / 100);
    if (newLoan > itemVal) newLoan = itemVal;
    const insRate = Math.max(0, parseFloat(formData.insuranceRate) || 0);
    setFormData((prev) => ({ ...prev, loanAmount: newLoan }));
    // An LTV chip changes the loan amount, so on the amount basis it can change
    // the band too — same re-ask as typing the amount by hand.
    if (applyAmountSlabRate(newLoan) === null) {
      const rate = Math.max(0, parseFloat(formData.interestRate) || 0);
      if (rate > 0) calculateInterest(rate, newLoan);
    }
    if (insRate > 0) calculateInsurance(insRate, newLoan);
    setErrors((prev) => ({ ...prev, loanAmount: undefined }));
  };

  // const calculateInterest = (rate) => {
  //   const safeRate = Math.max(0, rate);
  //   const loanAmount = Math.max(0, parseFloat(formData.loanAmount) || 0);
  //   const interestAmount = (loanAmount * safeRate) / 100;
  //   setFormData((prev) => ({
  //     ...prev,
  //     interestAmount: interestAmount,
  //   }));
  // };
  const calculateInterest = (rate, loanAmountOverride) => {
    const safeRate = Math.max(0, parseFloat(rate) || 0);
    // Use the explicitly-passed loan amount when available — reading
    // formData.loanAmount here would return the stale (pre-setState) value
    // when called from the loan-amount onChange handler.
    const loanAmount = Math.max(
      0,
      loanAmountOverride !== undefined
        ? parseFloat(loanAmountOverride) || 0
        : parseFloat(formData.loanAmount) || 0,
    );
    const interestAmount = (loanAmount * safeRate) / 100;
    // Round to nearest whole number
    const roundedInterest = Math.round(interestAmount);
    setFormData((prev) => ({
      ...prev,
      interestAmount: roundedInterest,
    }));
  };

  // const calculateInsurance = (rate) => {
  //   const safeRate = Math.max(0, parseFloat(rate) || 0);
  //   const loanAmount = Math.max(0, parseFloat(formData.loanAmount) || 0);
  //   const insuranceAmount = (loanAmount * safeRate) / 100;
  //   setFormData((prev) => ({
  //     ...prev,
  //     insuranceAmount: insuranceAmount.toFixed(2),
  //   }));
  // };
  const calculateInsurance = (rate, loanAmountOverride) => {
    const safeRate = Math.max(0, parseFloat(rate) || 0);
    // Use the explicitly-passed loan amount when available to avoid reading a
    // stale formData.loanAmount during the same render's onChange handler.
    const loanAmount = Math.max(
      0,
      loanAmountOverride !== undefined
        ? parseFloat(loanAmountOverride) || 0
        : parseFloat(formData.loanAmount) || 0,
    );
    const insuranceAmount = (loanAmount * safeRate) / 100;
    // Round to nearest whole number
    const roundedInsurance = Math.round(insuranceAmount);
    setFormData((prev) => ({
      ...prev,
      insuranceAmount: roundedInsurance,
    }));
  };

  // ── Scheme change: override interest with scheme rate ─────────────────────────
  // Which of the scheme's two base rates applies depends on the mode the payment
  // is being created in: official ⇒ "Interest (%)", unofficial ⇒ "Interest 2 (%)"
  // with a fallback to Interest when the scheme has no second rate.
  const handleSchemeChange = (schemeId) => {
    const scheme = schemes.find((s) => s.id === schemeId);
    if (scheme) {
      const rate = schemeBaseRateForMode(scheme, Number(pawnMode) === 2);
      setFormData((prev) => ({
        ...prev,
        scheme: schemeId,
        interestRate: rate,
      }));
      calculateInterest(rate);
    } else {
      setFormData((prev) => ({
        ...prev,
        scheme: schemeId,
        // Don't clear interest rate if scheme is being set to null/undefined
      }));
    }
  };

  const handleMetalTypeChange = (metalId) => {
    const metal = metalTypes.find((m) => m.id === metalId);
    if (!metal) return;
    setSelectedMetalId(metalId);
    // Clear the rate rather than carrying the old metal's over — the effect on
    // [selectedMetalId, formData.date] refills it with this metal's own rate
    // for the loan date, and an empty field is honest while that is in flight.
    setRatePerGram("");
    setRateSource(null);
    setCurrentOrnament((prev) => ({
      ...prev,
      metal: metal.name,
      metal_type_id: metalId,
      // Item types belong to a metal — GOLD's CHAIN is not SILVER's.
      item_type_id: "",
      itemName: "",
      // Every metal has its own idea of standard: gold opens at 91.6, silver
      // wherever its Default Purity is set on the Metal Type master.
      purity: defaultPurityOf(metal),
      metal_type_rate: "",
      value: "",
    }));
  };

  const handleRateChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setRatePerGram("");
      setRateSource(null);
      setCurrentOrnament((prev) => ({
        ...prev,
        metal_type_rate: "",
      }));
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return;
    if (num < 0) {
      setRatePerGram("0");
      setCurrentOrnament((prev) => ({ ...prev, metal_type_rate: 0 }));
      return;
    }

    setRatePerGram(value);
    setCurrentOrnament((prev) => ({ ...prev, metal_type_rate: num }));

    // A typed rate is a rate SET FOR THE LOAN DATE — that is the whole point of
    // keying it here rather than on a master. Debounced so the keystrokes of
    // "15000" become one write, and pinned to the date as it stands now so a
    // later change of loan date cannot retro-stamp what was already saved.
    if (storeId == null || !selectedMetalId) return;
    if (!isUsableLoanDate(formData.date)) return;
    const recordDate = formData.date;
    if (rateUpdateTimeoutRef.current) {
      clearTimeout(rateUpdateTimeoutRef.current);
    }
    rateUpdateTimeoutRef.current = setTimeout(async () => {
      // A fetch in flight is about to overwrite the field anyway; saving now
      // would store a figure the teller is not looking at.
      if (isFetchingRateRef.current) return;
      try {
        const result = await saveRateForMetal(num, recordDate);
        if (!result) return;
        setRateSource({ date: recordDate, exact: true });
        showSnackbar(result.message || "Rate saved", "success");
      } catch (error) {
        console.error("Failed to save rate:", error);
        showSnackbar("Failed to save rate", "error");
      }
    }, 1000);
  };

  // ── Edit existing payment ───────────────────────────────────────────────────
  // Map ornaments coming from the API (snake_case columns) into the shape the
  // ornament table/form uses.
  const mapApiOrnaments = (apiOrnaments) =>
    (apiOrnaments || []).map((o, idx) => {
      const metal = metalTypes.find((m) => m.id === o.metal_type_id);
      return {
        si: idx + 1,
        metal: metal?.name || "",
        metal_type_id: o.metal_type_id,
        stamp: false,
        item_type_id: o.item_type_id || "",
        // Joined back from Item Type Master by the API.
        itemName: o.item_name || "",
        // Backend column is item_pcs; keep `pieces` as the in-form field name.
        pieces: o.item_pcs != null ? parseInt(o.item_pcs, 10) : 1,
        grossWeight: o.item_gross != null ? parseFloat(o.item_gross) : "",
        netWeight: o.item_net != null ? parseFloat(o.item_net) : "",
        purity:
          o.purity_percentage != null ? parseFloat(o.purity_percentage) : "",
        metal_type_rate:
          o.metal_type_rate != null ? parseFloat(o.metal_type_rate) : "",
        value: o.item_value != null ? parseFloat(o.item_value) : "",
      };
    });

  // Look up an existing pawn payment by its ticket (voucher) number and load
  // every attached record into the form so it can be edited and re-saved.
  const loadPawnForEdit = async (rawVoucher) => {
    const voucherNo = (rawVoucher || "").trim();
    if (!voucherNo) {
      showSnackbar("Enter a pawn ticket number", "warning");
      return;
    }
    setLoadingTicket(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/payments/loan/${encodeURIComponent(
          voucherNo,
        )}`,
        { params: { store_id: storeId } },
      );
      if (!(res.data.success && res.data.statusCode === 200)) {
        showSnackbar(
          res.data.statusMessage || "Pawn ticket not found",
          "error",
        );
        return;
      }

      const { loan_info, customer_info, nominee_info, ornaments_info } =
        res.data.data;

      // Enforce the active pawn mode, using the SAME visibility rule as the
      // dashboard and the reports: status 3 (official-clean) is SHARED by both
      // books, so official mode loads status 1 + 3 and unofficial mode loads
      // status 2 + 3. Only status 1 is exclusive to official mode. A ticket the
      // active mode may not see reads as "not found" and never flips the mode.
      // Safe to edit from either mode: the BE takes an edit's series from the
      // existing row (`existing.status`), never the app mode, so opening a
      // status-3 ticket in unofficial mode can't move it into the other books.
      const recStatus = Number(loan_info?.status) || 1;
      const activeMode = Number(pawnMode) === 2 ? 2 : 1;
      const visibleInMode =
        activeMode === 2 ? recStatus === 2 || recStatus === 3 : recStatus !== 2;
      if (!visibleInMode) {
        showSnackbar("Pawn ticket not found", "error");
        return;
      }
      // Once any receipt has been raised against this loan the payment must
      // never be edited: changing it would alter the principal the receipt
      // chain was built on, silently corrupting every downstream
      // interest/settlement calculation. We still LOAD the ticket (so the
      // teller can view it and keep stepping through vouchers), but the whole
      // form is rendered read-only — see `paymentLocked`.
      let hasReceipts = false;
      try {
        const recRes = await axios.get(
          `${API_BASE_URL}/api/pawn/receipts/${storeId}/${encodeURIComponent(
            loan_info.voucher_no,
          )}`,
        );
        const existingReceipts = recRes.data?.data?.receipts;
        hasReceipts =
          Array.isArray(existingReceipts) && existingReceipts.length > 0;
      } catch (recErr) {
        // If the receipt lookup fails we can't prove the payment is safe to
        // edit, so err on the side of caution and lock it read-only anyway.
        console.error("Failed to check receipts before edit:", recErr);
        hasReceipts = true;
      }
      setPaymentLocked(hasReceipts);
      setLoanSettled(String(loan_info?.is_settled).toUpperCase() === "Y");

      // Customer documents → aadhar / pan numbers + photo / id-proof images
      const docs = customer_info?.documents || [];
      const aadharDoc = docs.find((d) => d.name === "Aadhar");
      const panDoc = docs.find((d) => d.name === "PAN");
      const photoDoc = docs.find((d) => d.name === "Photo");

      const sonOf =
        customer_info?.relative_relation &&
        customer_info.relative_relation !== "None" &&
        customer_info?.relative_name
          ? `${customer_info.relative_relation} ${customer_info.relative_name}`
          : customer_info?.relative_name || "";

      // Reconstruct the payment-mode selection from the stored amounts using the
      // new model columns (cash + up to two bank legs). Falls back to the legacy
      // single card_amount_received column for older rows.
      const cashAccId = getCashAccountId();
      const cashRecv = parseFloat(loan_info.cash_amount_received) || 0;
      const cardRecv1 =
        parseFloat(
          loan_info.card_amount_received_1 ?? loan_info.card_amount_received,
        ) || 0;
      const cardRecv2 = parseFloat(loan_info.card_amount_received_2) || 0;
      // Split when cash coexists with any bank leg, or two bank legs are present.
      const isSplit =
        (cashRecv > 0 && (cardRecv1 > 0 || cardRecv2 > 0)) ||
        (cardRecv1 > 0 && cardRecv2 > 0);
      const hasSecondBank = cardRecv2 > 0;
      if (isSplit) {
        setSplitPayment(true);
        setCashAmount(cashRecv);
        setBankAmount(cardRecv1);
        // Leg 2 restores the account actually stored on the payment (fall back
        // to the configured UPI-transfer account for legacy rows).
        setSecondAccountId(
          loan_info.bank_account_id_1 || getUpiAccountId() || "",
        );
        setUtrNumber(loan_info.transaction_ref_no_1 || "");
        if (hasSecondBank) {
          setSecondBankEnabled(true);
          setBankAmount2(cardRecv2);
          setThirdAccountId(loan_info.bank_account_id_2 || "");
          setTxnRefNumber(loan_info.transaction_ref_no_2 || "");
        } else {
          setSecondBankEnabled(false);
          setBankAmount2("");
          setThirdAccountId("");
          setTxnRefNumber("");
        }
      } else {
        setSplitPayment(false);
        setCashAmount("");
        setBankAmount("");
        setBankAmount2("");
        setSecondAccountId("");
        setSecondBankEnabled(false);
        setThirdAccountId("");
        setUtrNumber("");
        setTxnRefNumber("");
      }
      // Non-split bank-only payment keeps its bank account as the selected
      // payMode; otherwise default to cash.
      const payMode = isSplit
        ? cashAccId || ""
        : cardRecv1 > 0 && loan_info.bank_account_id_1
        ? loan_info.bank_account_id_1
        : cashAccId || "";

      setFormData((prev) => ({
        ...prev,
        voucherNo: loan_info.voucher_no,
        date: loan_info.transaction_date
          ? dayjs(loan_info.transaction_date).format("YYYY-MM-DD")
          : prev.date,
        paymentTime: loan_info.payment_time || prev.paymentTime,
        collectByDate: loan_info.collect_by_date
          ? dayjs(loan_info.collect_by_date).format("YYYY-MM-DD")
          : prev.collectByDate,
        customerId: customer_info?.id?.toString() || "",
        customerName: customer_info?.name || "",
        mobile: customer_info?.mobile?.toString() || "",
        address: customer_info?.pickup_address || customer_info?.address || "",
        aadhar: aadharDoc?.number || "",
        pan: panDoc?.number || "",
        sonOf,
        loanAmount: parseFloat(loan_info.loan_amount) || "",
        tareWeight:
          loan_info.tare_wt !== null && loan_info.tare_wt !== undefined
            ? parseFloat(loan_info.tare_wt)
            : "",
        interestRate: parseFloat(loan_info.loan_interest) || "",
        // Deduct-off rows store 0 insurance — fall back to the master rate
        // (and recompute the amount) so toggling "Deduct 1 month" on while
        // editing charges insurance correctly.
        insuranceRate:
          parseFloat(loan_info.insurance_percentage) ||
          parseFloat(pawnSettings?.master_ins) ||
          "",
        interestAmount: parseFloat(loan_info.interest_amount) || "",
        insuranceAmount:
          parseFloat(loan_info.insurance_amount) ||
          (parseFloat(pawnSettings?.master_ins) > 0
            ? Math.round(
                ((parseFloat(loan_info.loan_amount) || 0) *
                  parseFloat(pawnSettings.master_ins)) /
                  100,
              )
            : ""),
        otherCharges: parseFloat(loan_info.other_charges) || "",
        paymentAmount: parseFloat(loan_info.payment_amount) || "",
        scheme: loan_info.scheme_id || "",
        floorId: loan_info.floor_id || "",
        remarks: loan_info.remarks || "",
        chequeNo: loan_info.transaction_ref || "",
        payMode,
      }));

      setOrnaments(mapApiOrnaments(ornaments_info));
      // Loading a different loan must abandon any in-progress ornament edit.
      // Otherwise a stale editingIndex/currentOrnament left over from the
      // previously-loaded loan lets "Update" overwrite the wrong ornament of
      // the newly-loaded loan (a silent, false edit). Reset the edit index and
      // clear the item-specific fields (keeping metal/rate/purity defaults).
      setEditingIndex(null);
      setCurrentOrnament((prev) => ({
        ...prev,
        si: 0,
        stamp: false,
        itemName: "",
        pieces: 1,
        grossWeight: "",
        netWeight: "",
        value: "",
      }));
      grossAtNetSyncRef.current = null;
      setValueEdited(false);
      setCustomer(customer_info);
      setDeductOneMonth(loan_info.one_month_deduct === 1);

      // Nominee — set it for the UI now, and stash it so the nominee-list
      // fetch triggered by the customer-id change doesn't overwrite it with
      // the customer's primary nominee (see pendingNomineeRef).
      if (loan_info.nominee_id && nominee_info) {
        const ticketNominee = {
          nominee_id: loan_info.nominee_id,
          name: nominee_info.name || loan_info.nominee_name,
          mobile: nominee_info.mobile,
          relation: nominee_info.relative_relation,
          address: nominee_info.pickup_address,
        };
        setSelectedNominee(ticketNominee);
        pendingNomineeRef.current = {
          custId: customer_info?.id,
          nominee: ticketNominee,
        };
      } else {
        setSelectedNominee(null);
        pendingNomineeRef.current = null;
      }

      // Enter edit mode and pin this ticket's voucher number. The loaded
      // ticket's status is guaranteed to match the active pawnMode (enforced
      // above), so the mode is never changed here.
      setCurrentVoucherNo(loan_info.voucher_no);
      setCurrentFormattedVoucherNo(loan_info.voucher_no);
      // Reflect the ticket actually loaded in the search box (so stepping
      // forward/backward updates it, not the number originally typed).
      setTicketSearch(loan_info.voucher_no);
      setEditingPawnId(loan_info.id);
      setEditMode(true);
      // A freshly-loaded above-cap voucher starts masked until F2+O.
      setRevealInterest(false);
      setErrors({});
      setActiveField(null);
      setSuggestions({
        name: [],
        mobile: [],
        guardian: [],
        aadhar: [],
        pan: [],
      });
      setOpenSections({ 1: true, 2: true, 3: true, 4: true });

      // Images — article photos come straight off the loan row; the customer
      // photo / id-proof are fetched from their document URLs as base64 last
      // (these awaits must come after the synchronous state above).
      setImages({
        cust_img: null,
        article_img_1: loan_info.article_img_1 || null,
        article_img_2: loan_info.article_img_2 || null,
        cust_ID_img: null,
      });
      const photoUrl = realDocFile(photoDoc?.file);
      const idUrl = realDocFile(aadharDoc?.file) || realDocFile(panDoc?.file);
      setImageFileNames({
        cust_img: photoUrl ? "existing_photo.jpg" : "",
        article_img_1: loan_info.article_img_1 ? "article_1.jpg" : "",
        article_img_2: loan_info.article_img_2 ? "article_2.jpg" : "",
        cust_ID_img: idUrl ? "existing_id_proof.jpg" : "",
      });
      // Stored fingerprints arrive as base64 and are put straight back on the
      // payload when this ticket is saved again, so editing a loan without
      // re-scanning leaves the recorded impressions untouched.
      setFingerprints({
        fingerprint_1: loan_info.fingerprint_1
          ? { data: loan_info.fingerprint_1, source: "stored", quality: null }
          : null,
        fingerprint_2: loan_info.fingerprint_2
          ? { data: loan_info.fingerprint_2, source: "stored", quality: null }
          : null,
      });
      showSnackbar(
        `${canEditRecords ? "Editing" : "Viewing"} pawn ticket ${loan_info.voucher_no}`,
        "success",
      );
      if (photoUrl) {
        const b64 = await urlToBase64(photoUrl);
        if (b64) setImages((p) => ({ ...p, cust_img: b64 }));
      }
      if (idUrl) {
        const b64 = await urlToBase64(idUrl);
        if (b64) setImages((p) => ({ ...p, cust_ID_img: b64 }));
      }
    } catch (err) {
      showSnackbar(
        err.response?.data?.statusMessage || "Failed to load pawn ticket",
        "error",
      );
    } finally {
      setLoadingTicket(false);
    }
  };

  // Chevron navigation: walk tickets in CREATION order, not voucher order.
  // dir = "prev" (left chevron) or "next" (right chevron). The walk is anchored
  // on the pawn row currently loaded; with nothing loaded the form is sitting on
  // a brand-new ticket that conceptually comes after the newest saved one, so
  // "prev" lands on the most recent ticket and "next" has nowhere to go (the
  // right chevron is hidden in that state).
  //
  // Creation order is what makes this work under manual voucher entry too: the
  // teller may type MV22, then P14, then T34, so there is no sequence to step
  // through — only the order the tickets were booked in. With auto vouchers the
  // two orders coincide.
  const navigateTicket = async (dir) => {
    const forward = dir === "next";
    // Nothing loaded ⇒ we're already on the new-entry form; there is no ticket
    // ahead of it. (The right chevron isn't rendered here either.)
    if (forward && !editingPawnId) return;
    setLoadingTicket(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/payments/nav-step/${storeId}`,
        {
          params: {
            // The row the walk steps away from; omitted on a new-entry form.
            current_id: editingPawnId || undefined,
            // The walk stays inside the ACTIVE book's own tickets (official
            // 1 + 3, unofficial 2 + 3) — the only scope where everything it
            // hands back is something this mode is allowed to open.
            mode: pawnMode,
            dir,
          },
        },
      );
      if (res.data.success && res.data.voucher_no) {
        await loadPawnForEdit(res.data.voucher_no);
      } else if (forward) {
        // Stepped forward off the newest ticket. The only thing past it is the
        // ticket about to be written, so hand the teller a clean form — the
        // next-voucher effect refills the header the moment editMode drops, and
        // manual mode gets a blank voucher field to type into.
        clearAllFormData();
      } else {
        showSnackbar(
          res.data.statusMessage || "No earlier ticket found",
          "info",
        );
      }
    } catch (err) {
      showSnackbar(
        err.response?.data?.statusMessage || "Failed to navigate tickets",
        "error",
      );
    } finally {
      setLoadingTicket(false);
    }
  };

  // ─── Mobile-number lookup in the header search box ────────────────────────
  // The box takes a pawn ticket number; typing DIGITS also searches customers
  // by mobile (name / party code match too, since that is one search) and drops
  // them under the box. Picking one loads that customer's most recent loan —
  // the Payment-screen counterpart of the Receipt screen's last receipt and the
  // Settlement screen's last close-out. A customer with no loan in the current
  // book is reported by name in the dropdown instead.
  const customerLookup = useCustomerTxnLookup({
    storeId,
    pawnMode,
    kind: "loan",
    showSnackbar,
    onPick: async (txn) => {
      // Park the loaded ticket in the box, exactly like the chevron walk does,
      // so Enter on what is shown re-loads the same ticket.
      setTicketSearch(txn.voucher_no);
      await loadPawnForEdit(txn.voucher_no);
    },
  });

  // Leave edit mode and return to a clean "new payment" form.
  const handleExitEdit = () => {
    clearAllFormData();
  };

  // ── Manual-voucher derived state ─────────────────────────────────────────────
  // Editable only while creating (never when editing an existing payment).
  const manualVoucherMode = !!pawnSettings?.manual_voucher_no && !editMode;
  // Nothing typed yet. Save deliberately stays enabled here — pressing it is
  // how the teller finds out why the payment won't go through — but there is
  // no number to print, so printing stays out.
  const voucherMissing =
    manualVoucherMode && !String(currentFormattedVoucherNo ?? "").trim();
  // Hard block: the typed number is already used, or its check is still in
  // flight. Neither may be saved or printed.
  const voucherBlocked =
    manualVoucherMode && (voucherCheck.checking || voucherCheck.taken);

  // Typing in the manual voucher field drives both the raw and formatted
  // voucher (they are the same free-form string in manual mode) plus formData.
  const handleManualVoucherChange = (val) => {
    voucherTouchedRef.current = true;
    setVoucherWarn(false);
    setCurrentFormattedVoucherNo(val);
    setCurrentVoucherNo(val);
    setFormData((p) => ({ ...p, voucherNo: val }));
  };

  const handleSave = async () => {
    // A NEW loan has to record one branch, and while every branch is on screen
    // there is no honest way to choose. Editing a loan already on file is left
    // alone: it was attributed when it was raised, and correcting an ornament
    // weight is not a reason to reattribute it.
    if (branchBlocked && !editingPawnId) {
      openBranchPicker();
      return;
    }
    // Only the Store Head / HOD may change a payment already on file (this
    // also covers the Ctrl+Enter shortcut, not just the hidden button).
    if (editMode && !canEditRecords) {
      showSnackbar(VIEW_ONLY_MESSAGE, "error");
      return;
    }
    // A payment with receipts against it is view-only — never persist edits.
    if (paymentLocked) {
      showSnackbar(
        "Unable to edit payment as receipt(s) has been made against it",
        "error",
      );
      return;
    }
    // In manual-voucher mode, never persist a duplicate/blank/still-checking
    // number (also covers the keyboard Save shortcuts, not just the button).
    if (voucherMissing) {
      setVoucherWarn(true);
      showSnackbar(
        "Manual voucher mode is on — please enter a voucher number first",
        "error",
      );
      return;
    }
    if (voucherBlocked) {
      showSnackbar(
        voucherCheck.taken
          ? "That voucher number is already used — enter a different one"
          : "Please wait — checking the voucher number",
        "error",
      );
      return;
    }
    if (!validateForm()) {
      const msg =
        (!formData.customerId &&
          !formData.customerName?.trim() &&
          "Customer is required") ||
        ((!formData.loanAmount || parseFloat(formData.loanAmount) <= 0) &&
          "Loan amount is required") ||
        (ornaments.length === 0 && "Add at least one ornament before saving") ||
        ((!formData.paymentAmount || parseFloat(formData.paymentAmount) <= 0) &&
          "Payment amount is required") ||
        "Please fill all mandatory fields (marked *)";
      showSnackbar(msg, "error");
      return;
    }

    // Other charges are credited to the configured other-charges account —
    // without one the ledger legs can't net out.
    if (
      (parseFloat(formData.otherCharges) || 0) > 0 &&
      !pawnSettings?.other_charges_acc
    ) {
      showSnackbar(
        "Other Charges account is not configured. Set it in Pawn Settings to use Other Charges.",
        "error",
      );
      return;
    }

    // ── Split-payment integrity checks (so the legs net out on the ledger) ──
    if (splitPayment) {
      const cAmt = parseFloat(cashAmount) || 0;
      const b1Amt = parseFloat(bankAmount) || 0;
      const b2Amt = secondBankEnabled ? parseFloat(bankAmount2) || 0 : 0;
      const expected = parseFloat(formData.paymentAmount) || 0;
      const sum = cAmt + b1Amt + b2Amt;
      if (Math.abs(sum - expected) > 0.5) {
        showSnackbar(
          `Split amounts must total ₹${expected.toLocaleString(
            "en-IN",
          )} (currently ₹${sum.toLocaleString("en-IN")})`,
          "error",
        );
        return;
      }
      if (b1Amt > 0 && !secondAccountId) {
        showSnackbar("Select an account for the first transfer", "error");
        return;
      }
      if (b2Amt > 0 && !thirdAccountId) {
        showSnackbar("Select an account for the second transfer", "error");
        return;
      }
    }

    setLoading(true);

    // Stamp the payment time at the moment of saving — not when the screen was
    // opened — so the record reflects when the payment actually happened.
    const paymentTimeNow = dayjs().format("HH:mm:ss");
    setFormData((prev) => ({ ...prev, paymentTime: paymentTimeNow }));

    const extractRawNumber = (formattedVoucher) => {
      const match = formattedVoucher.match(/\d+$/);
      return match ? parseInt(match[0], 10) : 1;
    };

    try {
      const cashAccId = getCashAccountId();
      const isCashPayment = !splitPayment && formData.payMode === cashAccId;
      const isBankPayment = !splitPayment && formData.payMode !== cashAccId;

      // ── New pawn_broking payment model ──────────────────────────────────
      // pay_mode always represents the CASH leg ("CASH"); pay_mode_1 / pay_mode_2
      // represent up to two bank/account-transfer legs ("BANK" or null). The
      // amounts land in cash_amount_received / card_amount_received_1/2 and the
      // accounts in bank_account_id_1 / bank_account_id_2.
      let cashReceived = null; // cash_amount_received
      let cardReceived1 = null; // card_amount_received_1
      let cardReceived2 = null; // card_amount_received_2
      let payModeOne = null; // pay_mode_1
      let payModeTwo = null; // pay_mode_2
      let bankAccount1 = null; // bank_account_id_1
      let bankAccount2Id = null; // bank_account_id_2

      // ── Legacy fields (kept so the current controller still balances the
      // ledger until it is migrated to the new columns) ─────────────────────
      let cashPaymentAmount = null,
        cardPaymentAmount = null,
        selectedAccountId = null,
        secondPaymentAccountId = null;

      if (splitPayment) {
        const cAmt = parseFloat(cashAmount) || 0;
        const b1Amt = parseFloat(bankAmount) || 0;
        const b2Amt = secondBankEnabled ? parseFloat(bankAmount2) || 0 : 0;

        cashReceived = cAmt;
        // Leg 2 is the first account transfer; leg 3 (optional) is the 2nd.
        if (b1Amt > 0 && secondAccountId) {
          cardReceived1 = b1Amt;
          payModeOne = "BANK";
          bankAccount1 = secondAccountId;
        }
        if (secondBankEnabled && b2Amt > 0 && thirdAccountId) {
          cardReceived2 = b2Amt;
          payModeTwo = "BANK";
          bankAccount2Id = thirdAccountId;
        }

        // Legacy: fold both bank legs into the single card_payment so the
        // existing ledger math still nets out.
        cashPaymentAmount = cAmt;
        cardPaymentAmount = b1Amt + b2Amt;
        selectedAccountId = secondAccountId;
        secondPaymentAccountId = cashAccId;
      } else if (isCashPayment) {
        cashReceived = parseFloat(formData.paymentAmount) || 0;

        cashPaymentAmount = formData.paymentAmount;
        selectedAccountId = formData.payMode;
      } else if (isBankPayment) {
        cardReceived1 = parseFloat(formData.paymentAmount) || 0;
        payModeOne = "BANK";
        bankAccount1 = formData.payMode;

        cardPaymentAmount = formData.paymentAmount;
        selectedAccountId = formData.payMode;
      }
      const currentYear = defaultFY || new Date().getFullYear();
      const currentFYId = defaultFYId;
      const payload = {
        user_id: userId,
        store_id: storeId,
        // The branch that RAISED this loan. Attribution, not confinement — the
        // loan stays findable and receiptable from every branch of the store,
        // which is the whole cross-branch flow. Omitted rather than guessed
        // when the session has no branch of its own (a service role), because
        // the server then stamps the store's main branch, and that is a more
        // honest answer than whichever branch happened to be cached.
        ...(branchScope?.mode === "branch" && branchScope?.branchId != null
          ? { branch_id: branchScope.branchId }
          : {}),
        type: "P",
        cust_id: formData.customerId,
        loan_amount: formData.loanAmount,
        loan_intrest: formData.interestRate,
        transaction_date: formData.date,
        payment_amount: formData.paymentAmount,
        intrest_amount: formData.interestAmount,
        // Insurance is only ever charged when the first month is deducted up
        // front. With the deduct off the field is hidden in the UI, so never
        // let a stale computed rate/amount reach the row (columns are NOT NULL
        // DEFAULT 0, so send 0 rather than null).
        insurance_amount: deductOneMonth
          ? parseFloat(formData.insuranceAmount) || 0
          : 0,
        insurance_percentage: deductOneMonth
          ? parseFloat(formData.insuranceRate) || 0
          : 0,
        tare_wt: parseFloat(formData.tareWeight) || null,
        other_charges: parseFloat(formData.otherCharges) || 0,
        other_charges_account_id: pawnSettings?.other_charges_acc || null,
        cheque_card_no: formData.chequeNo,
        remarks: formData.remarks,
        payment_time: paymentTimeNow,
        scheme_id: formData.scheme,
        floor_id: formData.floorId || null,
        cash_payment: cashPaymentAmount,
        card_payment: cardPaymentAmount,
        ornaments,
        collect_by_date: formData.collectByDate,
        pawnMode,
        // ── Legacy ledger fields (current controller) ──
        account_id: selectedAccountId,
        second_account_id: secondPaymentAccountId,
        split_payment: splitPayment,
        // ── New pawn_broking payment-leg model ──
        // pay_mode always represents the CASH leg; the bank/account-transfer
        // legs go in pay_mode_1 / pay_mode_2 ("BANK") with their amounts in
        // cash_amount_received / card_amount_received_1 / card_amount_received_2
        // and accounts in bank_account_id_1 / bank_account_id_2.
        pay_mode: "CASH",
        pay_mode_1: payModeOne,
        pay_mode_2: payModeTwo,
        cash_amount_received: cashReceived,
        card_amount_received_1: cardReceived1,
        card_amount_received_2: cardReceived2,
        bank_account_id_1: bankAccount1,
        bank_account_id_2: bankAccount2Id,
        transaction_ref: formData.chequeNo,
        // Split-leg references: one transaction reference per transfer leg.
        transaction_ref_no_1: splitPayment ? utrNumber || null : null,
        transaction_ref_no_2:
          splitPayment && secondBankEnabled ? txnRefNumber || null : null,
        one_month_deduct: deductOneMonth ? 1 : 0,
        // Manual-voucher override: on create, when the store setting is on, send
        // the teller-typed number verbatim and flag it so the backend uses it
        // instead of the auto-sequence. Ignored while editing.
        manual_voucher: manualVoucherMode ? 1 : 0,
        voucher_no: manualVoucherMode
          ? String(currentFormattedVoucherNo ?? "").trim()
          : currentVoucherNo,
        article_img_1: images.article_img_1,
        article_img_2: images.article_img_2,
        // Base64 of each scanned finger, or null when that finger wasn't read —
        // the backend stores them as BLOBs (NULL for an unscanned finger).
        fingerprint_1: fingerprints.fingerprint_1?.data || null,
        fingerprint_2: fingerprints.fingerprint_2?.data || null,
        nominee_name: selectedNominee ? selectedNominee.name : "",
        nominee_id: selectedNominee ? selectedNominee.nominee_id : null,
        pledge_account_id: pawnSettings?.pledge_acc,
        interest_account_id: pawnSettings?.interest_acc,
        insurance_account_id: pawnSettings?.insurance_acc,
        cash_account_id: pawnSettings?.cash_acc,
        master_int: pawnSettings?.master_int ?? null,
        fy_id: currentFYId,
        finance_year: currentYear,
      };
      const response = editMode
        ? await axios.put(
            `${API_BASE_URL}/api/pawn/payments/${editingPawnId}`,
            payload,
          )
        : await axios.post(`${API_BASE_URL}/api/pawn/payments`, payload);
      if (response.data.success && response.data.statusCode === 200) {
        const savedVoucher = response.data.data?.voucher_no;
        const wasEdit = editMode;
        if (savedVoucher) {
          setCurrentFormattedVoucherNo(savedVoucher);
          setCurrentVoucherNo(extractRawNumber(savedVoucher));
        }
        showSnackbar(
          wasEdit
            ? "Payment updated successfully!"
            : "Payment saved successfully!",
          "success",
        );
        resetForm();
        await fetchNextVoucher();

        // The pouch barcode label goes out alongside the voucher, so the
        // counter gets both at once: the two voucher copies to hand over, and
        // the label to stick on the pouch holding the pledged ornaments.
        //
        // Which printer that is depends on the device. A phone or tablet with
        // its own Bluetooth printer paired prints there and nowhere else —
        // falling back to the bridge would drop the label on the counter's SATO
        // in another room, where nobody is standing. Everything else (and any
        // device that has not set one up) queues for the bridge exactly as
        // before.
        //
        // Neither may surface as a save error: the payment is already
        // committed, and an operator who reads a cold printer as a failed save
        // enters the pawn twice.
        if (savedVoucher) {
          const sent = await printLabelOverBluetooth(savedVoucher);
          // "skipped" means this device does not print over Bluetooth at all, so
          // the bridge was always the intended route. A *failed* Bluetooth print
          // falls through to the bridge as well, but only where the bridge's
          // printer is in the same room: on a handheld that would drop the label
          // at a counter nobody is standing at.
          const atCounter = !window.matchMedia?.("(pointer: coarse)")?.matches;
          if (sent === "skipped" || (sent === "failed" && atCounter))
            queueLabelPrint(savedVoucher, { silent: true });
        }

        // Navigate only on success — and last, so the label's own message is
        // still on this screen when it is sent.
        handlePrint({ ...payload, voucher_no: savedVoucher });
      } else {
        // Backend returned an error (e.g., statusCode 500)
        showSnackbar(
          response.data.statusMessage || "Failed to save payment",
          "error",
        );
      }
    } catch (error) {
      // Network or other axios error
      showSnackbar(
        error.response?.data?.statusMessage || "Failed to save payment",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (
      ornaments.length > 0 ||
      formData.customerId ||
      formData.paymentAmount > 0
    ) {
      requestConfirm({
        title: "Discard changes?",
        message:
          "Are you sure you want to cancel? All unsaved data will be lost.",
        confirmLabel: "Discard",
        confirmColor: "error",
        onConfirm: clearAllFormData,
      });
    } else clearAllFormData();
  };

  const handleNewPayment = () => {
    if (
      ornaments.length > 0 ||
      formData.customerId ||
      formData.paymentAmount > 0
    ) {
      requestConfirm({
        title: "Start a new payment?",
        message: "Current data will be cleared. Do you want to continue?",
        confirmLabel: "Start New",
        confirmColor: "primary",
        onConfirm: clearAllFormData,
      });
    } else clearAllFormData();
  };

  const handleCustomerCreated = (newCustomer) => {
    // Extract Aadhar and PAN from customer_documents
    let aadharNumber = "";
    let panNumber = "";

    if (
      newCustomer.customer_documents &&
      newCustomer.customer_documents.length > 0
    ) {
      const aadharDoc = newCustomer.customer_documents.find(
        (doc) => doc.name === "Aadhar",
      );
      const panDoc = newCustomer.customer_documents.find(
        (doc) => doc.name === "PAN",
      );

      if (aadharDoc) aadharNumber = aadharDoc.number || "";
      if (panDoc) panNumber = panDoc.number || "";
    }

    // Apply validation to aadhar and pan
    const aadharValidation = validateAadhar(aadharNumber);
    if (aadharValidation.error) {
      showSnackbar(`Aadhar: ${aadharValidation.error}`, "warning");
    }

    const panValidation = validatePAN(panNumber);
    if (panValidation.error) {
      showSnackbar(`PAN: ${panValidation.error}`, "warning");
    }

    // Build sonOf string from relative_relation and relative_name
    let sonOfValue = "";
    if (
      newCustomer.relative_relation &&
      newCustomer.relative_relation !== "None"
    ) {
      sonOfValue = `${newCustomer.relative_relation} ${
        newCustomer.relative_name || ""
      }`.trim();
    } else if (newCustomer.relative_name) {
      sonOfValue = newCustomer.relative_name;
    }

    setFormData((prev) => ({
      ...prev,
      customerId: newCustomer.id || newCustomer.customer_id,
      customerName: newCustomer.name,
      mobile: newCustomer.mobile?.toString() || "",
      address: newCustomer.pickup_address || "",
      sonOf: sonOfValue,
      aadhar: aadharValidation.value,
      pan: panValidation.value,
    }));

    setCustomer(newCustomer);

    // Also update images if any document has file
    if (newCustomer.customer_documents?.length) {
      const photoDoc = newCustomer.customer_documents.find(
        (doc) => doc.name === "Photo",
      );
      const aadharDoc = newCustomer.customer_documents.find(
        (doc) => doc.name === "Aadhar",
      );
      const panDoc = newCustomer.customer_documents.find(
        (doc) => doc.name === "PAN",
      );

      // Set customer photo
      const photoFile = realDocFile(photoDoc?.file);
      if (photoFile) {
        // If it's a URL, we need to convert it to base64 or keep as URL
        // For now, we'll keep as is and let the image component handle it
        setImages((prev) => ({ ...prev, cust_img: photoFile }));
        setImageFileNames((prev) => ({
          ...prev,
          cust_img: photoDoc.fileName || "customer_photo.jpg",
        }));
      }

      // Set ID proof (prefer Aadhar, fallback to PAN — whichever has a file)
      const idDoc = realDocFile(aadharDoc?.file)
        ? aadharDoc
        : realDocFile(panDoc?.file)
        ? panDoc
        : null;
      if (idDoc) {
        setImages((prev) => ({
          ...prev,
          cust_ID_img: realDocFile(idDoc.file),
        }));
        setImageFileNames((prev) => ({
          ...prev,
          cust_ID_img: idDoc.fileName || "id_proof.jpg",
        }));
      }
    }

    showSnackbar(
      `Customer "${newCustomer.name}" created and selected`,
      "success",
    );
  };

  // ── Persist a photo uploaded here as the customer's Photo document ──────────
  // When the loaded customer has no real photo on file (no Photo document, or
  // a Photo row whose file column is empty / "No file"), an image uploaded in
  // the Documents section is saved onto the customer record itself via the
  // update-customer API — so the photo follows the customer everywhere from
  // then on. An existing customer photo is never overwritten from this screen.
  // NOTE: the update API deletes any document row missing from the `documents`
  // array, so ALL existing docs are resent unchanged (with their doc_id)
  // alongside the Photo doc carrying the new file. Docs arrive in two shapes
  // depending on how the customer was loaded (mapped: doc_id/fileName/
  // renewalDate, or raw rows: id/file_name/renew_date) — both are normalized.
  const saveCustomerPhotoIfMissing = async (dataUrl, fileName) => {
    const custId = formData.customerId;
    if (!custId || !customer || !dataUrl?.startsWith("data:")) return;
    const rawDocs = customer.customer_documents || customer.documents || [];
    const photoDoc = rawDocs.find((d) => d.name === "Photo");
    if (realDocFile(photoDoc?.file)) return; // already has a photo — keep it
    const photoFileName = fileName || "customer_photo.jpg";
    const documents = rawDocs.map((d) => ({
      doc_id: d.doc_id || d.id,
      name: d.name,
      number: d.number || "",
      file: d.name === "Photo" ? dataUrl : d.file || null,
      fileName:
        d.name === "Photo" ? photoFileName : d.fileName || d.file_name || "",
      renewalDate: d.renewalDate || d.renew_date || null,
    }));
    if (!photoDoc)
      documents.push({
        name: "Photo",
        number: formData.mobile || "",
        file: dataUrl,
        fileName: photoFileName,
      });
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/pawn/customers/${custId}`,
        { store_id: storeId, documents },
      );
      if (res.data.success) {
        // Sync local state so a second upload doesn't fire another update,
        // guarding against the customer having changed while awaiting.
        const updatedDocs = res.data.data?.customer_documents;
        if (updatedDocs) {
          setCustomer((prev) => {
            if (!prev) return prev;
            const prevId = (prev.customer_id ?? prev.id)?.toString();
            if (prevId && prevId !== custId.toString()) return prev;
            const key = prev.customer_documents
              ? "customer_documents"
              : "documents";
            return { ...prev, [key]: updatedDocs };
          });
        }
        showSnackbar("Photo saved to customer's profile", "success");
      } else {
        showSnackbar(
          res.data.statusMessage || "Could not save photo to customer profile",
          "warning",
        );
      }
    } catch {
      // Non-blocking: the payment itself can still be saved — the photo just
      // stays local to this form.
      showSnackbar("Could not save photo to customer profile", "warning");
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    // Bake in EXIF orientation so phone photos don't come out rotated.
    const dataUrl = await normalizeImageFile(file);
    setImages((prev) => ({ ...prev, [field]: dataUrl }));
    setImageFileNames((prev) => ({ ...prev, [field]: file.name }));
    if (field === "cust_img") saveCustomerPhotoIfMissing(dataUrl, file.name);
  };

  // Image captured from the live camera (already upright; no EXIF fix needed).
  const handleCameraCapture = (dataUrl) => {
    if (!cameraField) return;
    setImages((prev) => ({ ...prev, [cameraField]: dataUrl }));
    setImageFileNames((prev) => ({
      ...prev,
      [cameraField]: "camera-photo.jpg",
    }));
    if (cameraField === "cust_img")
      saveCustomerPhotoIfMissing(dataUrl, "camera-photo.jpg");
  };

  const handleRemoveImage = (field, event) => {
    if (event) event.stopPropagation();
    if (field === "cust_img" || field === "cust_ID_img") {
      showSnackbar("Cannot delete customer image or ID image", "warning");
      return;
    }
    setImages((prev) => ({ ...prev, [field]: null }));
    setImageFileNames((prev) => ({ ...prev, [field]: "" }));
    showSnackbar(`${field.replace(/_/g, " ")} image removed`, "info");
  };

  // ── Fingerprints ────────────────────────────────────────────────────────────
  // A capture is held in memory until the ticket is saved; clearing one sends
  // an explicit null so the stored blob is wiped on the next save.
  const handleFingerprintCaptured = (field, capture) => {
    if (!field || !capture?.data) return;
    setFingerprints((prev) => ({ ...prev, [field]: capture }));
    const label =
      fingerprintFields.find((f) => f.key === field)?.label || "Fingerprint";
    showSnackbar(`${label} captured`, "success");
  };

  const handleClearFingerprint = (field, event) => {
    if (event) event.stopPropagation();
    setFingerprints((prev) => ({ ...prev, [field]: null }));
    const label =
      fingerprintFields.find((f) => f.key === field)?.label || "Fingerprint";
    showSnackbar(`${label} removed`, "info");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customerId && !formData.customerName?.trim())
      newErrors.customerName = "Customer is required";
    if (!formData.loanAmount || parseFloat(formData.loanAmount) <= 0)
      newErrors.loanAmount = "Loan amount is required";
    // At least one ornament must be added; rate is no longer mandatory.
    if (ornaments.length === 0)
      newErrors.ornaments = "Add at least one ornament before saving";
    if (!formData.paymentAmount || parseFloat(formData.paymentAmount) <= 0)
      newErrors.paymentAmount = "Payment amount is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePrint = () => {
    if (!validateForm()) {
      showSnackbar("Please fix validation errors", "error");
      return;
    }

    // Stamp the payment time at the moment of printing so the ticket reflects
    // when the payment actually happened, not when the screen was opened.
    const paymentTimeNow = dayjs().format("HH:mm:ss");
    setFormData((prev) => ({ ...prev, paymentTime: paymentTimeNow }));

    // Calculate payment breakdown
    const cashAccId = getCashAccountId();
    const isCashPayment = !splitPayment && formData.payMode === cashAccId;
    const isBankPayment = !splitPayment && formData.payMode !== cashAccId;

    let cashPaymentAmount = null;
    let bankPaymentAmount = null;

    if (splitPayment) {
      cashPaymentAmount = parseFloat(cashAmount) || 0;
      bankPaymentAmount =
        (parseFloat(bankAmount) || 0) +
        (secondBankEnabled ? parseFloat(bankAmount2) || 0 : 0);
    } else if (isCashPayment) {
      cashPaymentAmount = parseFloat(formData.paymentAmount) || 0;
      bankPaymentAmount = 0;
    } else if (isBankPayment) {
      cashPaymentAmount = 0;
      bankPaymentAmount = parseFloat(formData.paymentAmount) || 0;
    }

    // Format ornaments with all details
    const formattedOrnaments = ornaments.map((item, idx) => ({
      id: idx + 1,
      description: item.itemName || "",
      metal: item.metal || "",
      purity: item.purity || "",
      rate: item.metal_type_rate || 0,
      pcs: item.pieces || 1,
      weight: parseFloat(item.netWeight) || 0,
      grossWeight: parseFloat(item.grossWeight) || 0,
      value: parseFloat(item.value) || 0,
    }));

    // Get bank account name if applicable
    const getPaymentMethodName = () => {
      if (splitPayment) return "Split Payment (Cash + Bank)";
      if (isCashPayment) return "Cash";
      if (isBankPayment) {
        const account = bankAccounts.find(
          (acc) => acc.account_id === formData.payMode,
        );
        return account
          ? `Bank Transfer - ${account.account_name}`
          : "Bank Transfer";
      }
      return "Cash";
    };

    const ticketData = {
      // Store Info
      storeInfo: {
        name: currentStoreInfo?.name || "Pawn Shop",
        address: currentStoreInfo?.address || "",
        phone: currentStoreInfo?.owner_mobile || "",
        email: currentStoreInfo?.email || "",
        gst: currentStoreInfo?.gst_no || "",
      },

      // Ticket Info
      ticketInfo: {
        voucherNo:
          currentFormattedVoucherNo || currentVoucherNo || formData.voucherNo,
        date: formData.date,
        time: paymentTimeNow,
      },

      // Customer Info
      customerInfo: {
        id: formData.customerId,
        name: formData.customerName,
        mobile: formData.mobile,
        address: formData.address,
        aadhar: formData.aadhar,
        pan: formData.pan,
        sonOf: formData.sonOf,
        district: formData.district,
        ledgerNo: formData.ledgerNo,
      },

      // Loan Info
      loanInfo: {
        loanAmount: parseFloat(formData.loanAmount) || 0,
        itemValue: parseFloat(formData.itemValue) || 0,
        interestRate: maskRate(formData.interestRate),
        interestAmount: parseFloat(formData.interestAmount) || 0,
        // Only printed when the first month was actually deducted — see the
        // payload note above.
        insuranceRate: deductOneMonth
          ? parseFloat(formData.insuranceRate) || 0
          : 0,
        insuranceAmount: deductOneMonth
          ? parseFloat(formData.insuranceAmount) || 0
          : 0,
        otherCharges: parseFloat(formData.otherCharges) || 0,
        scheme:
          schemes.find((s) => s.id === formData.scheme)?.scheme_name || "",
        schemeId: formData.scheme,
        collectByDate: formData.collectByDate,
        remarks: formData.remarks,
      },

      // Payment Info
      paymentInfo: {
        paymentAmount: parseFloat(formData.paymentAmount) || 0,
        cashAmount: cashPaymentAmount,
        bankAmount: bankPaymentAmount,
        paymentMethod: getPaymentMethodName(),
        chequeNo: formData.chequeNo || "",
        splitPayment: splitPayment,
        deductOneMonth: deductOneMonth,
        bankAccountId: splitPayment
          ? secondAccountId
          : isBankPayment
          ? formData.payMode
          : null,
      },

      // Ornaments
      ornaments: formattedOrnaments,

      // Total Summary
      summary: {
        totalOrnamentValue: parseFloat(formData.itemValue) || 0,
        totalLoanAmount: parseFloat(formData.loanAmount) || 0,
        totalInterestAmount: parseFloat(formData.interestAmount) || 0,
        totalInsuranceAmount: deductOneMonth
          ? parseFloat(formData.insuranceAmount) || 0
          : 0,
        totalPaymentAmount: parseFloat(formData.paymentAmount) || 0,
        dueAmount:
          (parseFloat(formData.loanAmount) || 0) +
          (parseFloat(formData.interestAmount) || 0) -
          (parseFloat(formData.paymentAmount) || 0),
      },

      // Nominee Info
      nomineeInfo: selectedNominee
        ? {
            name: selectedNominee.name,
            relation: selectedNominee.relation,
            mobile: selectedNominee.mobile,
            address: selectedNominee.address,
          }
        : null,

      // Images for the printed ticket — customer's photo and the article/
      // ornament photo. The user may fill either article slot, so fall back to
      // the second slot when the first is empty. Either may be null; the ticket
      // generator shows only whichever is present.
      images: {
        customer: images.cust_img || null,
        ornament: images.article_img_1 || images.article_img_2 || null,
      },

      // Metadata
      meta: {
        pawnMode: pawnMode,
        userId: Number(userId),
        storeId: Number(storeId),
        generatedAt: new Date().toISOString(),
      },
    };

    navigate("/pawn/ticket-generator", { state: ticketData });
  };

  // ── Barcode sticker printing ─────────────────────────────────────────────────
  // Prints a sticker for the ornament pouch: a scannable Code128 of the loan's
  // voucher number plus the human-readable identification block beneath it
  // (ticket no, customer name/ID/mobile, payment date, the ornaments with their
  // gross weights, and the loan amount in figures and words).
  //
  // Only the VOUCHER NUMBER is encoded in the bars — it never changes, so a
  // later scan (e.g. on the Stock Check screen) always resolves the loan's
  // CURRENT details from the DB even after the payment is edited. The rest is
  // printed as text: a 1D Code128 carrying all of it would be metres wide and
  // unscannable, and a stale copy of editable data must never be the scan key.
  // Nothing is persisted here.
  // ── Real label printer path ────────────────────────────────────────────────
  // Queues the pouch label for the print bridge running on the counter PC. The
  // browser cannot write raw bytes to a printer port, so the backend merges the
  // store's PRN template with this loan and parks the bytes in pawn_print_jobs;
  // the bridge claims the job and writes them to the printer's Windows share.
  //
  // Resolves true when a job was queued. `silent` is used by the save path,
  // where a missing template must not look like a failed payment — the label is
  // an accessory to the save, not part of it.
  const queueLabelPrint = async (voucherNo, { silent = false } = {}) => {
    if (!voucherNo || !storeId) return false;
    try {
      const res = await axios.post(`${API_BASE_URL}/api/pawn/print-jobs`, {
        store_id: storeId,
        voucher_no: voucherNo,
        doc_type: "pouch_label",
        created_by: userId,
      });
      if (res.data?.success) {
        // "Queued", not "sent": this call only parks the bytes for the print
        // bridge to collect. Claiming the label reached the printer here is what
        // let a stopped printer look healthy for a whole session.
        //
        // A queue with nothing collecting it is the same silence with extra
        // steps, so a missing bridge is a warning, not a success.
        //
        // The server's own wording is preferred over composing one here: it is
        // the only side that knows *where* the label is going to come out. When
        // this shop's own printer is offline but a sibling branch is covering for
        // it — the owner raising a loan for a closed branch from whichever
        // counter is open — the label does print, just not in this room, and the
        // operator has to be told which room. That cannot be said from here.
        const online = res.data.data?.bridge_online !== false;
        const elsewhere = res.data.data?.printing_at;
        showSnackbar(
          res.data.message ||
            `Barcode label queued for ${
              res.data.data?.target_share || "the label printer"
            }`,
          online ? (elsewhere ? "info" : "success") : "warning",
        );
        return true;
      }
      return false;
    } catch (error) {
      // 409 means the store is not set up yet (no template / no share name) —
      // actionable, so it is always worth saying even during a silent save.
      const msg = error.response?.data?.message;
      if (error.response?.status === 409) {
        showSnackbar(msg || "Barcode label not configured", "warning");
      } else if (!silent) {
        showSnackbar(msg || "Could not send the barcode label", "error");
      }
      return false;
    }
  };

  // ── Browser label path ─────────────────────────────────────────────────────
  // What a phone gets instead of the print bridge.
  //
  // Bridge printing works by handing the printer raw PRN bytes over a Windows
  // share, which needs a PC on the counter. A phone paired with a Bluetooth
  // label printer has neither: its only route to the printer is Chrome → the
  // Android print service, which receives a rendered page. So the same design
  // is *drawn* here — from the store's own PRN template, using the layout
  // parseLabel() extracts and the token values the backend resolves, the two
  // inputs the Barcode Mapper's preview already runs on. Phone output and SATO
  // output therefore come from one design, not two.

  // The store's design and this loan's values for it, or null when the store
  // has no template / the voucher is not on the server / the language carries
  // no drawable layout (SBPL). Fetched once per print, then reused as the
  // operator changes media or design on the label page.
  const fetchLabelDesign = async (voucherNo) => {
    if (!storeId) return null;
    try {
      const t = (
        await axios.get(
          `${API_BASE_URL}/api/pawn/label-templates/store/${storeId}/default`,
        )
      ).data?.data;
      if (!t?.id || !t?.prn_source) return null;

      const parsed = parseLabel(t.prn_source, t.variant_no || 1);
      if (!parsed.elements.length) return null;

      const rendered = (
        await axios.get(
          `${API_BASE_URL}/api/pawn/label-templates/${
            t.id
          }/render/${encodeURIComponent(voucherNo)}`,
        )
      ).data?.data;

      return { parsed, resolved: rendered?.resolved || {} };
    } catch {
      return null;
    }
  };

  // The values the built-in sticker prints — the same set, in the same order,
  // as the store's SATO tag: store name over the QR, then voucher, customer,
  // gross weight, quantity, purity. Floor and date are not on that tag, so they
  // are not gathered here either.
  const stickerFields = (voucherNo) => ({
    voucherNo,
    storeName: currentStoreInfo?.name || "",
    customerName: formData.customerName || "",
    grossWeight: ornaments
      .reduce((sum, o) => sum + (parseFloat(o.grossWeight) || 0), 0)
      .toFixed(3),
    pieces: ornaments.reduce(
      (sum, o) => sum + (parseInt(o.pieces, 10) || 1),
      0,
    ),
    // The first ornament's own percentage — there is no purity name to print
    // any more, and the pouch only ever carried one figure anyway.
    purity: String(
      ornaments.find((o) => o.purity)?.purity ??
        currentOrnament.purity ??
        "",
    ),
  });

  // Choose between the store's design and the built-in sticker for this media,
  // and say why when the choice is not the one the operator would expect.
  //
  // "auto" is not a preference for the template: a design drawn for 89 mm tag
  // stock does not become a 50 mm pouch label by being scaled: every dimension
  // loses 44%, which takes 2 mm text under 1.2 mm and the barcode's narrow bar
  // under what a handheld scanner resolves. On mismatched media the sticker,
  // laid out for that media, is the more faithful answer.
  const renderLabelFor = async (voucherNo, design, media, mode) => {
    const declared = design?.parsed?.declared;
    const fits = design && templateFitsMedia(declared, media);
    const useTemplate =
      design && (mode === "template" || (mode === "auto" && fits));

    // A PRN that sets its width but not its length is normal — continuous
    // stock has no length to declare — so the note says "89.4 mm wide" rather
    // than inventing a height or printing "89.4 × null".
    const declaredSize =
      declared?.wMm && declared?.hMm
        ? `${declared.wMm} × ${declared.hMm} mm`
        : `${declared?.wMm} mm wide`;

    if (useTemplate) {
      const out = await labelToSVG({
        parsed: design.parsed,
        resolved: design.resolved,
        widthMm: declared?.wMm || media.w,
        heightMm: declared?.hMm || media.h,
      });
      if (out)
        return {
          svg: out.svg,
          note: fits
            ? ""
            : `The store template is ${declaredSize}, so it is being scaled to fit ` +
              `${media.w} × ${media.h} mm. Text and bars shrink with it.`,
        };
    }

    const sticker = await builtInStickerSVG(stickerFields(voucherNo), {
      widthMm: media.w,
      heightMm: media.h,
    });

    return {
      svg: sticker.svg,
      note:
        design && !fits && mode === "auto"
          ? `The store template is drawn for stock ${declaredSize}, too far off ` +
            `${media.w} × ${media.h} mm to print legibly — the standard sticker is ` +
            `being used instead. Pick "Store template" above to override.`
          : "",
    };
  };

  // ── Bluetooth label path ───────────────────────────────────────────────────
  // The third route to a printer, and the only local one a phone or tablet
  // actually has: the browser speaks BLE to the printer in the operator's bag
  // and pushes the same label as raw printer commands. No bridge, no PC, no
  // print dialog — so "save the payment and the label comes out" is as true on
  // a handheld as it is at the counter.
  //
  // The label itself is not re-designed for this path: the store's PRN template
  // is drawn to an SVG exactly as it is for the print dialog, then rasterised
  // and wrapped in the printer's own language. One design, three transports.

  // Keep the button's state honest while the printer connects, disconnects or
  // wanders out of range.
  useEffect(() => subscribeBtPrinter(setBtState), []);

  // Reopen the link as soon as the screen is up rather than on the first save:
  // reconnecting takes a second or two, and paying for it *after* the payment
  // is committed is exactly when it is most conspicuous. Silent by design —
  // this is not a user gesture, so it can only ever reuse an existing pairing.
  useEffect(() => {
    if (!loadBtSettings().enabled) return;
    reconnectBtPrinter().catch(() => {
      /* printer off or out of range — the save path reports it if it matters */
    });
  }, []);

  // The label for this device: the store's design where it fits the stock this
  // printer holds, the built-in sticker otherwise — the same choice the print
  // dialog makes, from the same two saved preferences.
  const buildDeviceLabelSVG = async (voucherNo) => {
    const media = mediaFromKey(
      readLabelPref(LABEL_MEDIA_KEY, mediaKey(DEFAULT_LABEL_MM)),
    );
    const mode = readLabelPref(LABEL_DESIGN_KEY, "auto");
    const design = voucherNo ? await fetchLabelDesign(voucherNo) : null;
    const { svg } = await renderLabelFor(voucherNo, design, media, mode);
    return { svg, media };
  };

  /**
   * Print one label on the paired Bluetooth printer.
   *
   * @returns {Promise<"printed"|"failed"|"skipped">} — three outcomes, not two,
   * because the caller has to tell "this device does not do local printing"
   * (fall back to the counter's print bridge) apart from "this device owns the
   * label and the print failed" (do not also print it in another room).
   */
  const printLabelOverBluetooth = async (
    voucherNo,
    { allowPick = false, requireEnabled = true, rethrow = false } = {},
  ) => {
    const cfg = loadBtSettings();
    if (!btSupport().supported) return "skipped";
    if (requireEnabled && !cfg.enabled) return "skipped";
    if (!cfg.deviceId && !allowPick && !getBtState().connected)
      return "skipped";
    // A switched-off radio is not a printer fault to report, it is this device
    // saying "not my transport" — so it is "skipped", which hands the label to
    // the print bridge, and not "failed", which keeps it. A counter PC that
    // paired a pocket printer once and now has a USB SATO is exactly this case,
    // and reading it as a failure is what stopped the label ever being queued.
    //
    // Not applied to allowPick, which now marks the only caller that *asked*
    // for Bluetooth — the printer dialog's test print. There the browser's own
    // "turn it on" prompt is the answer to the question the operator asked.
    if (!allowPick && !getBtState().connected && !(await btRadioAvailable()))
      return "skipped";

    try {
      const { svg, media } = await buildDeviceLabelSVG(voucherNo);
      const bytes = await buildLabelBytes({
        svg,
        widthMm: media.w,
        heightMm: media.h,
        settings: cfg,
      });
      await btPrint(bytes, { allowPick });
      showSnackbar(
        `Barcode label sent to ${cfg.deviceName || "the Bluetooth printer"}`,
        "success",
      );
      return "printed";
    } catch (error) {
      // The printer dialog shows the reason in place, next to the setting that
      // fixes it; everywhere else it goes to the snackbar — named as a printer
      // problem rather than a save problem, because by the time this runs the
      // payment is already committed and an operator who reads it as a failed
      // save will enter the pawn twice.
      if (rethrow) throw error;
      showSnackbar(
        error?.message || "Could not print on the Bluetooth printer",
        "warning",
      );
      return "failed";
    }
  };

  const handlePrintBarcode = async () => {
    const ticketNo =
      currentFormattedVoucherNo || currentVoucherNo || formData.voucherNo || "";
    if (!ticketNo) {
      showSnackbar(
        "Save the payment first — a barcode needs a ticket number",
        "warning",
      );
      return;
    }

    // Which printer this press means depends on where the operator is standing.
    // A coarse pointer means the device is in their hand, so the label belongs
    // to the printer paired with *it* — queueing the job would print it on the
    // counter PC's SATO, in another room. A mouse means the counter, where the
    // label printer is the one cabled to this PC.
    const handheld = !!window.matchMedia?.("(pointer: coarse)")?.matches;

    // An open Bluetooth link wins on any device: it was opened deliberately, so
    // it is the printer the operator is watching. A failure falls through rather
    // than ending the press with nothing — the operator still needs the label.
    if (getBtState().connected) {
      const sent = await printLabelOverBluetooth(ticketNo, {
        requireEnabled: false,
      });
      if (sent === "printed") return;
    }

    // Then the counter's own printer — a USB or network label printer, driven by
    // the print bridge as raw printer commands. It is tried *before* a paired
    // Bluetooth printer rather than after, because a pairing lives in this
    // browser's storage for ever: one afternoon spent testing a pocket printer
    // must not outrank the printer plugged into the machine months later.
    if (!handheld && (await queueLabelPrint(ticketNo))) return;

    // A Bluetooth printer that is paired but not currently connected, reached
    // silently. allowPick is deliberately not set: opening the browser's device
    // chooser here is what put a "turn on Bluetooth" panel in front of an
    // operator whose printer is in the USB port. Pairing belongs to the printer
    // dialog, where it is what the operator actually asked for.
    if (loadBtSettings().deviceId) {
      const sent = await printLabelOverBluetooth(ticketNo, {
        requireEnabled: false,
      });
      if (sent === "printed") return;
    }

    // The window is opened before the label is built, not after: two API round
    // trips stand between here and a finished SVG, and a window.open that far
    // from the click is a popup blocker's textbook case. It shows a placeholder
    // until the label is ready.
    const win = window.open("", "_blank");
    if (!win) {
      showSnackbar(
        "Popup blocked — allow popups to print the barcode",
        "error",
      );
      return;
    }
    win.document.write(
      "<!doctype html><meta name='viewport' content='width=device-width,initial-scale=1'>" +
        "<title>Label</title><body style='margin:0;background:#0F172A;color:#E2E8F0;" +
        "font:14px Arial,Helvetica,sans-serif;display:flex;align-items:center;" +
        "justify-content:center;height:100vh'>Preparing label…</body>",
    );

    try {
      const design = await fetchLabelDesign(ticketNo);

      const paint = async (key, mode, autoPrint) => {
        const media = mediaFromKey(key);
        const { svg, note } = await renderLabelFor(
          ticketNo,
          design,
          media,
          mode,
        );
        win.document.open();
        win.document.write(
          labelPrintHTML({
            svg,
            media,
            designMode: mode,
            designNote: note,
            mediaOptions: mediaOptionsFor(design?.parsed?.declared, media),
            title: `Label ${ticketNo}`,
            autoPrint,
            handheld,
          }),
        );
        win.document.close();
        // Re-attached after every write: document.open() replaces the document,
        // but the window — and anything hung off it — survives.
        win.onLabelOptions = (nextKey, nextMode) => {
          writeLabelPref(LABEL_MEDIA_KEY, nextKey);
          writeLabelPref(LABEL_DESIGN_KEY, nextMode);
          paint(nextKey, nextMode, false);
        };
      };

      // Which stock is loaded belongs to the device, not the store or the
      // login, so it is remembered per browser rather than on the server: the
      // counter PC keeps the tag roll its SATO holds, the phone keeps its pouch
      // labels — and the Bluetooth path reads the very same two preferences.
      await paint(
        readLabelPref(LABEL_MEDIA_KEY, mediaKey(DEFAULT_LABEL_MM)),
        readLabelPref(LABEL_DESIGN_KEY, "auto"),
        true,
      );
    } catch {
      win.close();
      showSnackbar("Could not build the barcode label", "error");
    }
  };

  const resetForm = () => {
    const masterInt = pawnSettings?.master_int;

    const currentMetalId = selectedMetalId;
    // The Purity % the teller was working at survives a reset — the next pledge
    // over the same counter is nearly always at the same standard.
    const currentPurity = currentOrnament.purity;
    const currentRate = ratePerGram;

    setFormData((prev) => ({
      ...prev,
      paymentAmount: "",
      chequeNo: "",
      remarks: "",
      paymentTime: dayjs().format("HH:mm:ss"),
      customerId: "",
      customerName: "",
      mobile: "",
      address: "",
      aadhar: "",
      pan: "",
      ledgerNo: "",
      loanAmount: "",
      itemValue: "",
      tareWeight: "",
      scheme: "",
      interestRate:
        masterInt !== undefined && masterInt !== null ? masterInt : "",
      insuranceRate:
        pawnSettings?.master_ins !== undefined &&
        pawnSettings?.master_ins !== null
          ? pawnSettings.master_ins
          : "",
      interestAmount: "",
      insuranceAmount: "",
      otherCharges: "",
      sonOf: "",
      floorId: "",
      payMode: pawnSettings?.cash_acc || cashAccountId || "",
    }));

    setOrnaments([]);
    setCustomer(null);
    setErrors({});
    setEditingIndex(null);
    setSuggestions({ name: [], mobile: [], guardian: [], aadhar: [], pan: [] });
    setActiveField(null);

    setNomineesList([]);
    setSelectedNominee(null);
    setSplitPayment(false);
    setSecondAccountId("");
    setCashAmount("");
    setBankAmount("");
    setBankAmount2("");
    setSecondBankEnabled(false);
    setThirdAccountId("");
    setUtrNumber("");
    setTxnRefNumber("");
    setImages({
      cust_img: null,
      article_img_1: null,
      article_img_2: null,
      cust_ID_img: null,
    });
    setImageFileNames({
      cust_img: "",
      article_img_1: "",
      article_img_2: "",
      cust_ID_img: "",
    });
    setFingerprints({ fingerprint_1: null, fingerprint_2: null });
    setOpenSections({ 1: true, 2: false, 3: false, 4: false });

    setEditMode(false);
    setEditingPawnId(null);
    setPaymentLocked(false);
    setLoanSettled(false);
    setTicketSearch("");
    // Emptying the box has to take its customer dropdown with it, or a stale
    // suggestion list / "no record found" line hangs over the fresh form.
    customerLookup.reset();

    if (metalTypes.length > 0) {
      // Keep the same metal and purity selection if they exist
      const metalId = currentMetalId || metalTypes[0].id;
      const metal = metalTypes.find((m) => m.id === metalId) || metalTypes[0];

      setSelectedMetalId(metalId);
      setCurrentOrnament({
        si: 0,
        metal: metal.name,
        metal_type_id: metalId,
        stamp: false,
        itemName: "",
        pieces: 1,
        grossWeight: "",
        netWeight: "",
        purity: currentPurity || defaultPurityOf(metal),
        metal_type_rate: currentRate ? parseFloat(currentRate) : "",
        value: "",
      });
    } else {
      setCurrentOrnament({
        si: 0,
        metal: "",
        metal_type_id: "",
        stamp: false,
        itemName: "",
        pieces: 1,
        grossWeight: "",
        netWeight: "",
        purity: "",
        metal_type_rate: currentRate ? parseFloat(currentRate) : "",
        value: "",
      });
    }
    grossAtNetSyncRef.current = null;
  };

  // opts.modeSwitched — the clear was forced by an official/unofficial switch
  // rather than asked for, so the snackbar has to say why the sheet emptied.
  const clearAllFormData = (opts = {}) => {
    const masterInt = pawnSettings?.master_int;
    setValueEdited(false);

    const currentMetalId = selectedMetalId;
    // The Purity % the teller was working at survives a reset — the next pledge
    // over the same counter is nearly always at the same standard.
    const currentPurity = currentOrnament.purity;
    const currentRate = ratePerGram;

    // A cleared form in manual mode is a fresh ticket: the number goes back to
    // blank rather than carrying over whatever was last typed or loaded.
    //
    // A mode switch blanks it too, manual or not: the loaded ticket's number
    // belongs to the OTHER voucher series, so leaving it in the header until
    // the next-voucher fetch lands would show a number this book doesn't own.
    const manualNew = !!pawnSettings?.manual_voucher_no;
    const blankVoucher = manualNew || !!opts?.modeSwitched;
    if (blankVoucher) {
      voucherTouchedRef.current = false;
      setVoucherWarn(false);
      setCurrentVoucherNo(null);
      setCurrentFormattedVoucherNo("");
      if (opts?.modeSwitched) setAutoVoucherNo(null);
    }

    setFormData({
      voucherNo: blankVoucher ? "" : currentVoucherNo || "",
      date: dayjs().format("YYYY-MM-DD"),
      paymentTime: dayjs().format("HH:mm:ss"),
      customerId: "",
      customerName: "",
      mobile: "",
      phone: "",
      address: "",
      aadhar: "",
      pan: "",
      district: "",
      ledgerNo: "",
      sinNo: "",
      loanAmount: "",
      itemValue: "",
      tareWeight: "",
      scheme: "",
      interestRate:
        masterInt !== undefined && masterInt !== null ? masterInt : "",
      insuranceRate:
        pawnSettings?.master_ins !== undefined &&
        pawnSettings?.master_ins !== null
          ? pawnSettings.master_ins
          : "",
      interestAmount: "",
      insuranceAmount: "",
      otherCharges: "",
      paymentAmount: "",
      chequeNo: "",
      nominee: "",
      remarks: "",
      sonOf: "",
      collectByDate: dayjs().add(1, "year").format("YYYY-MM-DD"),
      floorId: "",
      payMode: pawnSettings?.cash_acc || cashAccountId || "",
    });

    setOrnaments([]);
    setCustomer(null);
    setErrors({});
    setEditingIndex(null);
    setSuggestions({ name: [], mobile: [], guardian: [], aadhar: [], pan: [] });
    setActiveField(null);

    setImages({
      cust_img: null,
      article_img_1: null,
      article_img_2: null,
      cust_ID_img: null,
    });
    setImageFileNames({
      cust_img: "",
      article_img_1: "",
      article_img_2: "",
      cust_ID_img: "",
    });
    setFingerprints({ fingerprint_1: null, fingerprint_2: null });

    const firstMetal = metalTypes[0];
    if (firstMetal) {
      setSelectedMetalId(currentMetalId || firstMetal.id);
      setCurrentOrnament({
        si: 0,
        metal: firstMetal.name,
        metal_type_id: firstMetal.id,
        stamp: false,
        itemName: "",
        pieces: 1,
        grossWeight: "",
        netWeight: "",
        purity: currentPurity || defaultPurityOf(firstMetal),
        metal_type_rate: currentRate ? parseFloat(currentRate) : "",
        value: "",
      });
    } else {
      setSelectedMetalId("");
      setCurrentOrnament({
        si: 0,
        metal: "",
        metal_type_id: "",
        stamp: false,
        itemName: "",
        pieces: 1,
        grossWeight: "",
        netWeight: "",
        purity: "",
        metal_type_rate: currentRate ? parseFloat(currentRate) : "",
        value: "",
      });
    }
    grossAtNetSyncRef.current = null;

    setOpenSections({ 1: true, 2: false, 3: false, 4: false });

    setEditMode(false);
    setEditingPawnId(null);
    setPaymentLocked(false);
    setLoanSettled(false);
    setTicketSearch("");
    // Emptying the box has to take its customer dropdown with it, or a stale
    // suggestion list / "no record found" line hangs over the fresh form.
    customerLookup.reset();

    showSnackbar(
      opts?.modeSwitched
        ? "Form cleared. Ready for a new payment."
        : "Form cleared. Ready for new payment.",
      "info",
    );
  };

  const loanAmt = parseFloat(formData.loanAmount) || 0;
  const intAmt = parseFloat(formData.interestAmount) || 0;
  const payAmt = parseFloat(formData.paymentAmount) || 0;
  const totalDue = loanAmt + intAmt;
  const balanceDue = totalDue - payAmt;
  const isFullyPaid = balanceDue === 0 && payAmt > 0;

  // ─── Official-mode interest-cap masking (F2+O reveals; matches receipt) ────
  // A LOADED above-cap voucher in official mode shows the cap (1.16%) instead
  // of its true rate until the operator reveals it with F2+O. While masked the
  // Interest % field is read-only so the hidden true rate can't be clobbered.
  // New payments being typed are never masked.
  const isOfficialMode = Number(pawnMode) === 1;
  const trueInterestRate = Number(formData.interestRate) || 0;
  const isAboveCap = trueInterestRate > OFFICIAL_RATE_CAP;
  const maskInterest =
    editMode && isOfficialMode && isAboveCap && !revealInterest;
  const displayInterestRate = maskInterest
    ? OFFICIAL_RATE_CAP
    : formData.interestRate;
  // Faint secret mark on the field whenever the loaded voucher is above cap in
  // official mode — a signal only the informed operator will notice.
  const showSecretMark = editMode && isOfficialMode && isAboveCap;
  // Mask any rate the same way for the printed ticket, so an official-mode
  // ticket never reveals an above-cap rate on paper (mirrors the receipt
  // screen's ticket masking; applies to fresh saves too, not just edits).
  const maskRate = (rate) => {
    const r = Number(rate) || 0;
    return isOfficialMode && r > OFFICIAL_RATE_CAP && !revealInterest
      ? OFFICIAL_RATE_CAP
      : r;
  };

  // ── Step indicator (derived only — no new hooks; purely presentational) ──────
  // Colours the numbered badge on each section header: green = done,
  // amber = the next section to fill, grey = not reached yet.
  const stepDone = {
    1: !!formData.customerId,
    2: ornaments.length > 0,
    3: loanAmt > 0 && payAmt > 0,
  };
  const activeStep = [1, 2, 3, 4].find((s) => !stepDone[s]) || 0;
  const stepStateOf = (s) =>
    stepDone[s] ? "done" : s === activeStep ? "active" : "todo";

  // ── Section accordion helpers ──────────────────────────────────────────────
  const toggleSection = (n) =>
    setOpenSections((prev) => ({ ...prev, [n]: !prev[n] }));

  // Auto-advance to ornaments when customer is selected
  useEffect(() => {
    if (formData.customerId) {
      setOpenSections((prev) => ({ ...prev, 1: false, 2: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customerId]);

  // ── Section summary strings ────────────────────────────────────────────────
  const sectionSummary = {
    1: formData.customerId
      ? `${formData.customerName}${
          formData.mobile ? " · " + formData.mobile : ""
        }`
      : "No customer selected",
    2:
      ornaments.length > 0
        ? `${ornaments.length} item${
            ornaments.length !== 1 ? "s" : ""
          } · ${ornaments
            .reduce((s, o) => s + (parseFloat(o.netWeight) || 0), 0)
            .toFixed(2)} g · ₹${Number(formData.itemValue || 0).toLocaleString(
            "en-IN",
          )}`
        : "No items added",
    3:
      loanAmt > 0
        ? `₹${loanAmt.toLocaleString("en-IN")}${
            formData.itemValue && parseFloat(formData.itemValue) > 0
              ? " · LTV " +
                Math.round((loanAmt / parseFloat(formData.itemValue)) * 100) +
                "%"
              : ""
          }${
            schemes.find((s) => s.id === formData.scheme)?.scheme_name
              ? " · " +
                schemes.find((s) => s.id === formData.scheme).scheme_name
              : ""
          }`
        : "Pending",
    4: (() => {
      const photos = Object.values(images).filter(Boolean).length;
      const fps = Object.values(fingerprints).filter(Boolean).length;
      if (!photos && !fps) return "Optional";
      const bits = [];
      if (photos)
        bits.push(`${photos} photo${photos !== 1 ? "s" : ""} uploaded`);
      if (fps) bits.push(`${fps}/2 fingerprints`);
      return bits.join(" · ");
    })(),
  };

  // ─── Shared select sx ────────────────────────────────────────────────────────
  const selectSx = {
    borderRadius: "8px",
    bgcolor: SURFACE,
    fontSize: "0.875rem",
    color: INK,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#9CA3AF", // Darker gray
      borderWidth: "2px", // Thicker - changed from 1.5px
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: BLUE_MED,
      borderWidth: "2.5px", // Thicker - changed from 2px
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BLUE,
      borderWidth: "3px", // Thicker - changed from 2.5px
    },
  };

  // Also update the labelSx for better consistency
  const labelSx = {
    fontSize: "0.8rem",
    color: INK_MUTED,
    "&.Mui-focused": { color: BLUE_DARK },
    "&.MuiInputLabel-shrink": {
      backgroundColor: SURFACE,
      padding: "0 4px",
    },
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={appTheme}>
      {/* One padded shell, not two. These used to be nested boxes each with
          their own padding, so a 360px phone lost 48px of width to gutters, and
          the inner `minHeight: 100vh` sat *inside* the outer's padding and
          overflowed the viewport by exactly that much. */}
      <Box
        ref={formRef}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          bgcolor: BG,
          minHeight: "100%",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <PageHeader
            isMobile={isMobile}
            editMode={editMode}
            pawnMode={pawnMode}
            manualVoucherMode={manualVoucherMode}
            currentFormattedVoucherNo={currentFormattedVoucherNo}
            handleManualVoucherChange={handleManualVoucherChange}
            voucherCheck={voucherCheck}
            voucherWarn={voucherWarn}
            currentVoucherNo={currentVoucherNo}
            navigateTicket={navigateTicket}
            canGoForward={!!editingPawnId}
            loadingTicket={loadingTicket}
            ticketSearch={ticketSearch}
            setTicketSearch={setTicketSearch}
            loadPawnForEdit={loadPawnForEdit}
            customerLookup={customerLookup}
            formData={formData}
            setFormData={setFormData}
            paymentLocked={formLocked}
            roleLocked={roleLocked}
            receiptLocked={paymentLocked}
            loading={loading}
            handleSave={handleSave}
            voucherBlocked={voucherBlocked}
            handleCancel={handleCancel}
            handleNewPayment={handleNewPayment}
            loanSettled={loanSettled}
          />

          {/* ── Main Grid ────────────────────────────────────────────────────── */}
          {/* When a receipt exists the form is view-only. Read-only is enforced
              per section (SectionCard `readOnly` makes each section's content
              inert), so section headers still collapse, the right-hand preview
              stays visible and the previous-loans rail stays clickable — the
              teller keeps every privilege except editing. */}
          <Box>
            {/* The 9/3 split only works once there is room for both: at md
                (900px) the right-hand ticket preview came out ~225px wide and
                its two-per-row fields collapsed into unreadable slivers. It now
                stacks under the form until lg, where 3 columns is ~300px. */}
            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {/* LEFT / MAIN COLUMN */}
              <Grid item xs={12} lg={9}>
                {/* ── Customer Section ───────────────────────────────────────── */}
                <CustomerSection
                  stepStateOf={stepStateOf}
                  openSections={openSections}
                  toggleSection={toggleSection}
                  sectionSummary={sectionSummary}
                  paymentLocked={formLocked}
                  isMobile={isMobile}
                  setLookupOpen={setLookupOpen}
                  setDialogExistingCustomerId={setDialogExistingCustomerId}
                  setDialogExistingCustomerName={setDialogExistingCustomerName}
                  setNewCustomerOpen={setNewCustomerOpen}
                  formData={formData}
                  setFormData={setFormData}
                  setNomineesList={setNomineesList}
                  errors={errors}
                  nameInputRef={nameInputRef}
                  mobileInputRef={mobileInputRef}
                  handleSearchableFieldChange={handleSearchableFieldChange}
                  handleFieldFocus={handleFieldFocus}
                  closeSuggestions={closeSuggestions}
                  suggestionLoading={suggestionLoading}
                  activeField={activeField}
                  suggestions={suggestions}
                  selectCustomer={selectCustomer}
                />

                {/* ── Ornaments Section ───────────────────────────────────────── */}
                <OrnamentsSection
                  stepStateOf={stepStateOf}
                  openSections={openSections}
                  toggleSection={toggleSection}
                  sectionSummary={sectionSummary}
                  paymentLocked={formLocked}
                  ornaments={ornaments}
                  selectedMetalId={selectedMetalId}
                  handleMetalTypeChange={handleMetalTypeChange}
                  metalTypes={metalTypes}
                  setCurrentOrnament={setCurrentOrnament}
                  currentOrnament={currentOrnament}
                  ratePerGram={ratePerGram}
                  rateSource={rateSource}
                  loanDate={formData.date}
                  handleRateChange={handleRateChange}
                  grossAtNetSyncRef={grossAtNetSyncRef}
                  showSnackbar={showSnackbar}
                  addOrnamentBtnRef={addOrnamentBtnRef}
                  setValueEdited={setValueEdited}
                  editingIndex={editingIndex}
                  addOrnament={addOrnament}
                  errors={errors}
                  isMobile={isMobile}
                  editOrnament={editOrnament}
                  deleteOrnament={deleteOrnament}
                  formData={formData}
                  selectSx={selectSx}
                  labelSx={labelSx}
                  itemTypes={itemTypes}
                  onItemTypeCreated={handleItemTypeCreated}
                  itemTypeError={itemTypeError && !currentOrnament.item_type_id}
                  // Where a type created from here is recorded; none in the
                  // all-branches view, and the server uses the main branch.
                  itemTypeBranchId={
                    branchScope?.mode === "branch" ? branchScope?.branchId ?? null : null
                  }
                  storeId={storeId}
                  userId={userId}
                  itemTypePickerRef={itemTypePickerRef}
                  onItemTypeCreateCancelled={() => {
                    addAfterItemTypeRef.current = false;
                  }}
                />

                {/* ── Loan & Payment ──────────────────────────────────────────── */}
                <LoanPaymentSection
                  stepStateOf={stepStateOf}
                  openSections={openSections}
                  toggleSection={toggleSection}
                  sectionSummary={sectionSummary}
                  paymentLocked={formLocked}
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  loanAmountRef={loanAmountRef}
                  calculateInterest={calculateInterest}
                  calculateInsurance={calculateInsurance}
                  applyLtv={applyLtv}
                  schemes={schemes}
                  handleSchemeChange={handleSchemeChange}
                  amountWiseInterest={amountWiseInterest}
                  applyAmountSlabRate={applyAmountSlabRate}
                  interestSlabHint={interestSlabHint}
                  pawnMode={pawnMode}
                  displayInterestRate={displayInterestRate}
                  maskInterest={maskInterest}
                  showSecretMark={showSecretMark}
                  revealInterest={revealInterest}
                  onSecretReveal={toggleRevealInterest}
                  floorsList={floorsList}
                  deductOneMonth={deductOneMonth}
                  setDeductOneMonth={setDeductOneMonth}
                  pawnSettings={pawnSettings}
                  splitPayment={splitPayment}
                  setSplitPayment={setSplitPayment}
                  getSplitTotal={getSplitTotal}
                  setCashAmount={setCashAmount}
                  setBankAmount={setBankAmount}
                  setBankAmount2={setBankAmount2}
                  setSecondBankEnabled={setSecondBankEnabled}
                  setThirdAccountId={setThirdAccountId}
                  setUtrNumber={setUtrNumber}
                  setTxnRefNumber={setTxnRefNumber}
                  setSecondAccountId={setSecondAccountId}
                  getUpiAccountId={getUpiAccountId}
                  getFilteredAccounts={getFilteredAccounts}
                  getCashAccountId={getCashAccountId}
                  loadingAccounts={loadingAccounts}
                  nomineeOptions={nomineeOptions}
                  selectedNominee={selectedNominee}
                  setSelectedNominee={setSelectedNominee}
                  setDialogExistingCustomerId={setDialogExistingCustomerId}
                  setDialogExistingCustomerName={setDialogExistingCustomerName}
                  setNewCustomerOpen={setNewCustomerOpen}
                  saveButtonRef={saveButtonRef}
                  bankAccounts={bankAccounts}
                  cashAmount={cashAmount}
                  secondBankEnabled={secondBankEnabled}
                  handleCash3={handleCash3}
                  handleCashAmountChange={handleCashAmountChange}
                  bankAmount={bankAmount}
                  handleBank1_3={handleBank1_3}
                  handleBankAmountChange={handleBankAmountChange}
                  utrNumber={utrNumber}
                  handleAddSecondBank={handleAddSecondBank}
                  thirdAccountId={thirdAccountId}
                  secondAccountId={secondAccountId}
                  bankAmount2={bankAmount2}
                  handleBank2_3={handleBank2_3}
                  txnRefNumber={txnRefNumber}
                  handleRemoveSecondBank={handleRemoveSecondBank}
                  selectSx={selectSx}
                  labelSx={labelSx}
                />

                {/* ── Documents Section ───────────────────────────────────────── */}
                <DocumentsSection
                  stepStateOf={stepStateOf}
                  openSections={openSections}
                  toggleSection={toggleSection}
                  sectionSummary={sectionSummary}
                  paymentLocked={formLocked}
                  imageFields={imageFields}
                  images={images}
                  setImageSourceDialog={setImageSourceDialog}
                  handleRemoveImage={handleRemoveImage}
                  handleImageUpload={handleImageUpload}
                  imageSourceDialog={imageSourceDialog}
                  setCameraField={setCameraField}
                  cameraField={cameraField}
                  handleCameraCapture={handleCameraCapture}
                  fingerprintFields={fingerprintFields}
                  fingerprints={fingerprints}
                  fingerprintDialog={fingerprintDialog}
                  setFingerprintDialog={setFingerprintDialog}
                  handleFingerprintCaptured={handleFingerprintCaptured}
                  handleClearFingerprint={handleClearFingerprint}
                />
              </Grid>

              {/* RIGHT COLUMN */}
              <SummaryPanel
                formData={formData}
                loanAmt={loanAmt}
                intAmt={intAmt}
                payAmt={payAmt}
                schemes={schemes}
                deductOneMonth={deductOneMonth}
                saveButtonRef={saveButtonRef}
                saveHidden={roleLocked}
                loading={loading}
                handleSave={handleSave}
                voucherBlocked={voucherBlocked}
                voucherMissing={voucherMissing}
                handlePrint={handlePrint}
                handlePrintBarcode={handlePrintBarcode}
                btSupported={btSupport().supported}
                btState={btState}
                onOpenBtPrinter={() => setBtPrinterOpen(true)}
                storeId={storeId}
                pawnMode={pawnMode}
                currentFormattedVoucherNo={currentFormattedVoucherNo}
                currentVoucherNo={currentVoucherNo}
                loadPawnForEdit={loadPawnForEdit}
              />
            </Grid>
          </Box>

          {/* ── Dialogs ────────────────────────────────────────────────────────── */}
          <Dialog
            open={confirmState.open}
            onClose={closeConfirm}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle sx={{ fontWeight: 700, color: INK }}>
              {confirmState.title}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: INK_SOFT }}>
                {confirmState.message}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
              <Button
                onClick={closeConfirm}
                sx={{ textTransform: "none", color: INK_MUTED }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color={
                  confirmState.confirmColor === "error" ? "error" : "primary"
                }
                onClick={handleConfirmAccept}
                sx={{ textTransform: "none", minWidth: 110 }}
              >
                {confirmState.confirmLabel}
              </Button>
            </DialogActions>
          </Dialog>
          <BluetoothPrinterDialog
            open={btPrinterOpen}
            onClose={() => setBtPrinterOpen(false)}
            // The test prints the real label for whatever ticket is loaded, and
            // a sample one otherwise: proving the transport is only half of it,
            // the operator also has to see that the design lands on the stock.
            onTestPrint={async () => {
              const ticketNo =
                currentFormattedVoucherNo ||
                currentVoucherNo ||
                formData.voucherNo ||
                "TEST-0000";
              await printLabelOverBluetooth(String(ticketNo), {
                allowPick: true,
                requireEnabled: false,
                rethrow: true,
              });
            }}
          />
          <CustomerLookupDialog
            open={lookupOpen}
            onClose={() => setLookupOpen(false)}
            onSelect={selectCustomer}
            storeId={storeId}
          />
          <NewCustomerDialog
            open={newCustomerOpen}
            onClose={() => setNewCustomerOpen(false)}
            onCustomerCreated={handleCustomerCreated}
            onNomineeCreated={() => {
              const cid = dialogExistingCustomerId || formData.customerId;
              if (cid) fetchNominees(cid);
            }}
            storeId={storeId}
            userId={userId}
            showSnackbar={showSnackbar}
            otpRequired={Boolean(pawnSettings?.OTP_for_cust)}
            allowDuplicateMobile={Boolean(
              pawnSettings?.allow_duplicate_mobile,
            )}
            existingCustomerId={dialogExistingCustomerId}
            existingCustomerName={dialogExistingCustomerName}
          />

          {/* ── Snackbar ───────────────────────────────────────────────────────── */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
              zIndex: 9999,
              position: "fixed",
              bottom: 16,
              left: 0,
              right: 0,
            }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              variant="filled"
              sx={{
                borderRadius: "10px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                width: "100%",
                maxWidth: "400px",
                margin: "0 auto",
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Opens only if Save was pressed on a new loan while every branch
              was on screen. Renders nothing otherwise. */}
          {branchGuardDialog}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PaymentScreen;
